import { createBodyHeatmapFromEntries, createEmptyBodyHeatmap, formatMuscleLabel } from "./bodyHeatmap.js";
import {
  escapeHtml,
  formatDateTime,
  formatDateWithWeekday,
  formatWeekdayLabel,
  normalizeDateKey,
} from "./lib/formatters.js";
import {
  renderHtmlAttributes,
  safeRendererAttributeValue,
  safeRendererClassName,
  safeRendererClassList,
  safeRendererIntegerAttributeValue,
  safeRendererPercentStyle,
  safeRendererStyleDeclaration,
  safeRendererStyleDeclarations,
} from "./lib/trustedRenderer.js";
import {
  MAX_PERCENT_1RM,
  MAX_PROGRAM_LENGTH_DAYS,
  MAX_REPS,
  MAX_REST_SEC,
  MAX_SETS_PER_EXERCISE,
  MAX_WEIGHT,
  MIN_REST_SEC,
} from "./constants/workoutConstraints.js";

// Trusted renderer contract:
// - This module returns HTML strings only and never mutates the DOM directly.
// - main.js must mount these renderers only through setElementHtml/replaceElementOuterHtml.
// - Untrusted text or attribute values must pass through escapeHtml before interpolation.
// - Locally-derived class/style tokens must pass through web/js/lib/trustedRenderer.js.
// - Dynamic data/id/class tokens must pass through renderSafeAttrValue/renderSafeIndexAttr/renderSafeClassList.

function renderSafeAttrValue(value, fallback = "") {
  return safeRendererAttributeValue(value, fallback);
}

function renderSafeIndexAttr(value, fallback = "0") {
  return safeRendererIntegerAttributeValue(value, fallback);
}

function renderSafeClassList(values = [], fallback = "") {
  return escapeHtml(safeRendererClassList(values, fallback));
}

const SEARCH_ACTION_BUTTON_ICON = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
`;

function renderSearchActionButton({
  id = "",
  classNames = [],
  kicker = "",
  value = "",
  valueId = "",
  attrs = {},
}) {
  const safeId = String(id || "").trim();
  const safeValueId = String(valueId || "").trim();

  return `
    <button type="button" class="${renderSafeClassList(classNames)}"${safeId ? ` id="${renderSafeAttrValue(safeId)}"` : ""}${renderHtmlAttributes(attrs)}>
      <span class="analytics-chart-change-exercise-copy">
        <span class="analytics-chart-change-exercise-kicker">${escapeHtml(kicker)}</span>
        <strong class="analytics-chart-change-exercise-value"${safeValueId ? ` id="${renderSafeAttrValue(safeValueId)}"` : ""}>${escapeHtml(value)}</strong>
      </span>
      ${SEARCH_ACTION_BUTTON_ICON}
    </button>
  `;
}

function renderCollapsibleLogCard({
  workoutId,
  isExpanded = false,
  logContext = "date",
  titleLines = [],
  expandedContentHtml = "",
}) {
  const safeWorkoutId = String(workoutId || "");

  return `
    <div class="${renderSafeClassList(["list-item", "log-card", isExpanded ? "is-expanded" : ""])}" data-workout-id="${escapeHtml(safeWorkoutId)}">
      <button
        class="log-card-header"
        data-role="toggle-workout-log"
        data-log-context="${escapeHtml(String(logContext || "date"))}"
        data-workout-id="${escapeHtml(safeWorkoutId)}"
        aria-expanded="${isExpanded ? "true" : "false"}"
      >
        <span class="log-card-title-wrap">
          ${titleLines.join("")}
        </span>
        <span class="log-card-chevron" aria-hidden="true">
          <span class="log-card-chevron-text">${isExpanded ? "접기" : "상세 보기"}</span>
          <svg class="log-card-chevron-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6"></path>
          </svg>
        </span>
      </button>
      ${isExpanded ? expandedContentHtml : ""}
    </div>
  `;
}

function formatWorkoutSourceLabel(source) {
  const sourceAlias = String(source?.dayAlias || "").trim();
  if (sourceAlias) {
    return sourceAlias;
  }
  if (source?.type === "program") {
    const dayIndex = Number(source?.dayIndex);
    if (Number.isInteger(dayIndex) && dayIndex >= 0) {
      return `DAY ${dayIndex + 1}`;
    }
    return "프로그램 루틴";
  }
  if (source?.type === "manual") {
    return "오늘 운동";
  }
  return String(source?.type || "오늘 운동");
}

function getBodyHeatColor(ratio) {
  const safeRatio = Math.max(0, Math.min(1, Number(ratio) || 0));
  if (safeRatio <= 0) {
    return "#edf2f7";
  }

  const alpha = 0.18 + safeRatio * 0.72;
  return `rgba(220, 38, 38, ${alpha.toFixed(3)})`;
}

function findHeatmapRegion(regions, key) {
  return (regions || []).find((region) => region?.key === key) || { ratio: 0, score: 0, label: "" };
}

function mc(ratio) {
  return safeRendererStyleDeclarations(
    [
      ["fill", getBodyHeatColor(ratio)],
      ["stroke", "rgba(0,0,0,0.08)"],
      ["stroke-width", "0.6"],
    ],
    "fill:#edf2f7;stroke:rgba(0,0,0,0.08);stroke-width:0.6",
  );
}

let bodyHeatmapFigureSequence = 0;

function dl(d) {
  return `<path d="${d}" fill="none" stroke="rgba(60,50,50,0.13)" stroke-width="0.7" stroke-linecap="round"/>`;
}

function renderBodyHeatmapFigure(view, regions) {
  bodyHeatmapFigureSequence += 1;
  const ids = { clip: `${view}-body-clip-${bodyHeatmapFigureSequence}` };
  const humanOutline = "M 80,10 C 72,10 68,16 68,24 C 68,31 71,34 73,37 C 73,42 65,45 54,50 C 44,54 36,55 34,65 C 32,73 33,83 35,93 C 37,101 31,109 29,119 C 27,131 30,141 32,147.6 C 34,154.1 38,154.1 40,147.6 C 42,138 40,127 40,118 C 40,108 46,99 46,89 C 46,79 50,73 54,68 C 56,81 54,101 58,114 C 60,121 57,125 55,133 C 52,147.6 52,171 56,190.5 C 58,203.5 63,210 62,223 C 61,236 57,255.5 59,271.1 C 60,281.5 63,285.4 66,284.1 C 69,282.8 70,277.6 69,268.5 C 67,255.5 70,238.6 73,225.6 C 76,212.6 78,191.8 79,160.6 C 79,148.9 80,143 80,143 C 80,143 81,148.9 81,160.6 C 82,191.8 84,212.6 87,225.6 C 90,238.6 93,255.5 91,268.5 C 90,277.6 91,282.8 94,284.1 C 97,285.4 100,281.5 101,271.1 C 103,255.5 99,236 98,223 C 97,210 102,203.5 104,190.5 C 108,171 108,147.6 105,133 C 103,125 100,121 102,114 C 106,101 104,81 106,68 C 110,73 114,79 114,89 C 114,99 120,108 120,118 C 120,127 118,138 120,147.6 C 122,154.1 126,154.1 128,147.6 C 130,141 133,131 131,119 C 129,109 123,101 125,93 C 127,83 128,73 126,65 C 124,55 116,54 106,50 C 95,45 87,42 87,37 C 89,34 92,31 92,24 C 92,16 88,10 80,10 Z";

  const defs = `<defs><clipPath id="${ids.clip}"><path d="${humanOutline}" /></clipPath></defs>`;
  const base = `<path d="${humanOutline}" fill="#e2e8f0" stroke="rgba(148,163,184,0.34)" stroke-width="1.2"/>`;

  let innerLeft = "";
  let innerMid = "";

  const R = (k) => findHeatmapRegion(regions, k).ratio;

  if (view === "front") {
    const shoulderRatio = Math.max(R("front_delts"), R("side_delts"), R("shoulders"));
    const absRatio = R("abs");
    const forearmsRatio = R("forearms");

    const muscles = [
      `<path d="M 80.0 34L 50.0 48L 80.0 52Z" style="${mc(R("traps"))}" />`,
      `<circle cx="43.0" cy="62" r="16" style="${mc(shoulderRatio)}" />`,
      `<path d="M 80.0 50C 65.0 50 51.0 56 49.0 70C 50.0 85 64.0 92 80.0 86Z" style="${mc(R("chest"))}" />`,
      `<path d="M 49.0 70C 60.0 92 75.0 96 80.0 96L 80.0 86C 64.0 92 50.0 85 49.0 70Z" style="${mc(R("chest"))}" />`,
      `<path d="M 46.0 85C 40.0 100 45.0 115 50.0 125L 54.0 110Z" style="${mc(R("lats"))}" />`,
      `<path d="M 46.0 85C 40.0 100 46.0 120 54.0 135L 60.0 145L 80.0 145L 80.0 158L 49.8 158C 46.0 130 50.0 100 54.0 90Z" style="${mc(absRatio * 0.6)}" />`,
      ...[82, 90, 98].map(y => `<rect x="52" y="${y}" width="10" height="3" rx="1.5" transform="rotate(-15, 52, ${y})" style="${mc(absRatio)}" />`),
      ...[100, 110, 120].map(y => `<rect x="71" y="${y}" width="8" height="8" rx="3" style="${mc(absRatio)}" />`),
      `<rect x="72" y="130" width="7" height="10" rx="3" style="${mc(absRatio)}" />`,
      `<ellipse cx="38.0" cy="85" rx="8" ry="16" style="${mc(R("biceps"))}" />`,
      `<ellipse cx="44.0" cy="98" rx="4" ry="10" style="${mc(R("biceps") * 0.6)}" />`,
      `<path d="M 43.0 105C 30.0 110 28.0 135 32.0 145L 40.0 145C 45.0 130 46.0 115 43.0 105Z" style="${mc(forearmsRatio)}" />`,
      `<ellipse cx="34.0" cy="120" rx="6" ry="18" fill="#dbe4ee" stroke="rgba(0,0,0,0.04)" />`,
      `<path d="M 50.0 145C 42.6 177.5 48.1 210 55.8 223L 64.8 223C 59.9 190.5 61.0 151.5 66.0 145Z" style="${mc(R("quads"))}" />`,
      `<ellipse cx="63.4" cy="178.8" rx="9" ry="40.6" style="${mc(R("quads"))}" />`,
      `<ellipse cx="71.5" cy="217.7" rx="6" ry="23.7" style="${mc(R("quads"))}" />`,
      `<path d="M 60.3 229.5C 68.2 239.9 67.2 249 61.0 262C 60.2 268.5 60.4 275 61.6 278.9L 58.5 278.9C 57.8 262 58.2 242.5 60.3 229.5Z" style="${mc(R("calves") * 0.9)}" />`,
      `<path d="M 58.0 232.1C 50.5 242.5 50.1 258.1 54.0 271.1C 55.2 275 56.4 277.6 58.5 278.9L 61.6 278.9C 60.2 268.5 58.4 249 58.0 232.1Z" style="${mc(R("calves"))}" />`
    ];

    const details = [
      `<path d="M 75.0 55.0C 72.0 70.0 75.0 80.0 80.0 86.0" stroke="rgba(0,0,0,0.12)" stroke-width="1.2" fill="none" />`,
      `<path d="M 49.0 70C 60.0 88 75.0 90 80.0 83.0" stroke="rgba(0,0,0,0.2)" stroke-width="1.2" fill="none" />`,
      `<path d="M 80.0 50L 80.0 96L 60.0 90" stroke="rgba(0,0,0,0.1)" stroke-width="1" fill="none" />`,
      `<path d="M 80.0 48Q 65.0 46 54.0 55" stroke="rgba(0,0,0,0.12)" stroke-width="1" fill="none" />`,
      `<path d="M 55.0 90Q 50.0 100 56.0 110" stroke="rgba(0,0,0,0.15)" stroke-width="1" fill="none" />`,
      `<path d="M 58.0 100Q 52.0 110 58.0 120" stroke="rgba(0,0,0,0.12)" stroke-width="1" fill="none" />`,
      `<path d="M 45.0 125Q 40.0 100 48.0 88" stroke="rgba(0,0,0,0.12)" stroke-width="1" fill="none" />`,
      ...[108, 118, 128].map(y => `<path d="M 80 ${y} Q 75 ${y + 2} 71 ${y}" stroke="rgba(0,0,0,0.1)" stroke-width="1" fill="none" />`),
      `<path d="M 80.0 143Q 70.0 145 60.0 140" stroke="rgba(0,0,0,0.12)" stroke-width="1.2" fill="none" />`,
      `<path d="M 64.0 151.5Q 59.0 184 55.8 223" stroke="rgba(0,0,0,0.08)" stroke-width="1" fill="none" />`,
      `<path d="M 67.8 171Q 70.5 197 68.1 216.5" stroke="rgba(0,0,0,0.08)" stroke-width="1" fill="none" />`,
      `<path d="M 65.0 155Q 62.0 180 65.0 205" stroke="rgba(0,0,0,0.1)" stroke-width="1.2" fill="none" />`,
      `<ellipse cx="60.6" cy="248.1" rx="4" ry="5.1" stroke="rgba(0,0,0,0.15)" stroke-width="1" fill="none" />`,
      `<path d="M 56.9 229.5C 59.2 232.1 61.4 232.1 63.6 229.5" stroke="rgba(0,0,0,0.12)" stroke-width="1" fill="none" />`,
      `<path d="M 59.2 232.1C 58.4 249 57.8 262 60.5 278.9" stroke="rgba(0,0,0,0.12)" stroke-width="1.2" fill="none" />`,
      `<path d="M 63.7 236C 66.0 245.1 65.1 252.9 61.0 262" stroke="rgba(0,0,0,0.1)" stroke-width="1" fill="none" />`,
      `<path d="M 40.0 70Q 35.0 85 38.0 100" stroke="rgba(0,0,0,0.1)" stroke-width="1" fill="none" />`
    ];
    innerLeft = muscles.concat(details).join("");
    innerMid = [
      `<path d="M 80.0 85L 80.0 145" stroke="rgba(0,0,0,0.1)" stroke-width="0.8" fill="none" />`
    ].join("");
  } else {
    const latsRatio = R("lats");
    const upperBackRatio = R("upper_back");
    const trapsRatio = R("traps");
    const lowerBackRatio = R("lower_back");
    const forearmsRatio = R("forearms");
    const rearDeltsRatio = Math.max(R("rear_delts"), R("shoulders"));
    const hamstringsRatio = R("hamstrings");

    const muscles = [
      `<path d="M 80.0 32C 70.0 38 52.0 45 50.0 48C 65.0 55 75.0 70 80.0 85Z" style="${mc(trapsRatio)}"/>`,
      `<path d="M 50.0 48 C 38.0 52 32.0 65 34.0 75 C 36.0 82 40.0 86 46.0 85 C 50.0 75 52.0 65 55.0 52 Z" style="${mc(rearDeltsRatio)}"/>`,
      `<path d="M 55.0 52C 55.0 65 65.0 75 80.0 85C 75.0 70 65.0 60 55.0 52Z" style="${mc(upperBackRatio)}"/>`,
      `<path d="M 55.0 68C 38.0 90 52.0 125 80.0 135C 75.0 110 70.0 90 80.0 85Z" style="${mc(latsRatio)}"/>`,
      `<path d="M 70.0 110C 65.0 125 70.0 135 75.0 135L 80.0 135L 80.0 100Z" style="${mc(lowerBackRatio)}"/>`,
      `<path d="M 46.0 85 C 40.0 85 34.0 85 33.0 95 C 31.0 105 29.0 115 30.0 120 C 35.0 125 40.0 120 40.0 118 C 43.0 105 46.0 95 46.0 85 Z" style="${mc(R('triceps'))}"/>`,
      `<path d="M 30.0 120 C 27.0 130 30.0 140 32.0 147.6 C 36.0 147.6 40.0 138 40.0 118 C 35.0 120 32.0 118 30.0 120 Z" style="${mc(forearmsRatio)}"/>`,
      `<path d="M 80.0 135C 56.0 135 46.8 158 50.9 177.5C 61.1 184 74.8 177.5 80.0 177.5Z" style="${mc(R('glutes'))}"/>`,
      `<path d="M 50.9 177.5C 42.9 203.5 47.9 223 53.5 229.5C 62.5 216.5 66.1 197 74.8 177.5Z" style="${mc(hamstringsRatio)}"/>`,
      `<path d="M 60.3 226.9C 53.5 226.9 48.1 239.9 50.9 252.9C 53.5 262 57.0 268.5 58.3 275L 61.5 275C 60.0 262 60.4 242.5 60.3 226.9Z" style="${mc(R('calves'))}"/>`,
      `<path d="M 60.3 226.9C 67.0 226.9 70.4 239.9 67.3 255.5C 64.3 265.9 62.4 271.1 61.5 275L 58.3 275C 58.9 262 60.4 242.5 60.3 226.9Z" style="${mc(R('calves') * 0.95)}"/>`
    ];

    const details = [
      `<path d="M 80.0 85C 70.0 70 60.0 55 50.0 48" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 80.0 135C 55.0 125 45.0 90 55.0 68" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 75.0 115C 65.0 100 58.0 80 50.0 52" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 80.0 100C 72.0 110 70.0 120 70.0 135" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 80.0 65C 68.0 68 62.0 62 55.0 52" stroke="rgba(0,0,0,0.12)" stroke-width="1" fill="none" />`,
      `<path d="M 46.0 85 C 40.0 82 35.0 80 34.0 75" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 40.0 85 C 38.0 100 36.0 110 35.0 120" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 35.0 120 C 32.0 130 34.0 140 36.0 145" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 30.0 118 C 35.0 118 38.0 118 40.0 118" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 50.9 177.5C 61.1 184 74.8 177.5 80.0 177.5" stroke="rgba(0,0,0,0.2)" stroke-width="1.2" fill="none" />`,
      `<path d="M 66.0 180Q 62.0 200 64.0 220" stroke="rgba(0,0,0,0.1)" stroke-width="1" fill="none" />`,
      `<path d="M 58.0 185Q 55.0 205 52.0 225" stroke="rgba(0,0,0,0.12)" stroke-width="1" fill="none" />`,
      `<path d="M 53.5 225.6C 56.9 228.2 61.4 228.2 64.8 225.6" stroke="rgba(0,0,0,0.12)" stroke-width="1" fill="none" />`,
      `<path d="M 60.3 226.9C 59.3 239.9 58.4 249 60.0 262" stroke="rgba(0,0,0,0.15)" stroke-width="1" fill="none" />`,
      `<path d="M 60.0 262C 59.1 268.5 59.4 275 60.6 281.5" stroke="rgba(0,0,0,0.15)" stroke-width="1.2" fill="none" />`,
      `<path d="M 50.4 239.9C 48.5 249 52.3 258.1 56.9 265.9" stroke="rgba(0,0,0,0.1)" stroke-width="1" fill="none" />`,
      `<path d="M 68.2 239.9C 70.5 249 66.3 260.7 62.3 268.5" stroke="rgba(0,0,0,0.1)" stroke-width="1" fill="none" />`
    ];
    innerLeft = muscles.concat(details).join("");
    innerMid = [
      `<path d="M 80.0 32L 80.0 135" stroke="rgba(0,0,0,0.15)" stroke-width="1" fill="none" />`,
      `<path d="M 80.0 135L 80.0 177.5" stroke="rgba(0,0,0,0.2)" stroke-width="1.5" fill="none" />`
    ].join("");
  }

  return `<svg class="home-heatmap-figure-svg" viewBox="0 0.0 160 295" aria-hidden="true">${defs}${base}<g clip-path="url(#${ids.clip})">${innerMid}<g>${innerLeft}</g><g transform="translate(160, 0) scale(-1, 1)">${innerLeft}</g></g></svg>`;
}


function renderBodyHeatmapCard(bodyHeatmap, options = {}) {
  const renderWhenEmpty = options?.renderWhenEmpty === true;
  const safeBodyHeatmap =
    bodyHeatmap && Array.isArray(bodyHeatmap.front) && Array.isArray(bodyHeatmap.back)
      ? {
        ...createEmptyBodyHeatmap(),
        ...bodyHeatmap,
        summary: Array.isArray(bodyHeatmap.summary) ? bodyHeatmap.summary : [],
      }
      : renderWhenEmpty
        ? createEmptyBodyHeatmap()
        : null;

  if (!safeBodyHeatmap) {
    return "";
  }

  const title = String(options?.title || "오늘 자극 부위").trim() || "오늘 자극 부위";
  const subtitle = String(
    options?.subtitle || "진한 빨강일수록 오늘 루틴에서 세트 비중이 높은 부위입니다.",
  ).trim();
  const emptySummaryText = String(options?.emptySummaryText || "").trim();
  const wrapperClassName = safeRendererClassName(options?.wrapperClassName, "card home-heatmap-card")
    || "card home-heatmap-card";
  const activeRegionCount = Array.from(
    new Set(
      [...safeBodyHeatmap.front, ...safeBodyHeatmap.back]
        .filter((entry) => Number(entry?.score) > 0)
        .map((entry) => entry.key),
    ),
  ).length;
  const leadEntry = safeBodyHeatmap.summary[0] || null;
  const supportEntry = safeBodyHeatmap.summary[1] || null;
  const summaryEntries = safeBodyHeatmap.summary.slice(0, 3);
  const hiddenSummaryCount = Math.max(0, safeBodyHeatmap.summary.length - summaryEntries.length);
  const leadText = leadEntry
    ? supportEntry
      ? `${leadEntry.label} 중심 · ${supportEntry.label} 보조`
      : `${leadEntry.label} 중심`
    : subtitle;
  const overviewHtml = leadEntry
    ? `
        <div class="home-heatmap-overview" aria-label="오늘 자극 부위 요약">
          <div class="home-heatmap-overview-item is-primary">
            <span class="home-heatmap-overview-label">TOP 1</span>
            <strong class="home-heatmap-overview-value">${escapeHtml(leadEntry.label)}</strong>
          </div>
          <div class="home-heatmap-overview-item">
            <span class="home-heatmap-overview-label">비중</span>
            <strong class="home-heatmap-overview-value">${escapeHtml(String(leadEntry.share))}%</strong>
          </div>
          <div class="home-heatmap-overview-item">
            <span class="home-heatmap-overview-label">활성</span>
            <strong class="home-heatmap-overview-value">${escapeHtml(String(activeRegionCount))}부위</strong>
          </div>
        </div>
      `
    : "";

  const summaryHtml =
    summaryEntries.length > 0
      ? summaryEntries
        .map(
          (entry, index) => `
        <div class="${renderSafeClassList(["home-heatmap-pill", index === 0 ? "is-primary" : ""])}">
          <span class="home-heatmap-pill-dot" style="${safeRendererStyleDeclaration("background", getBodyHeatColor(entry.ratio), "background:#edf2f7")}"></span>
          <span class="home-heatmap-pill-label">${escapeHtml(entry.label)}</span>
          <strong class="home-heatmap-pill-value">${escapeHtml(String(entry.share))}%</strong>
        </div>
      `,
        )
        .join("") + (hiddenSummaryCount > 0
          ? `<span class="home-heatmap-summary-more">+${escapeHtml(String(hiddenSummaryCount))}</span>`
          : "")
      : emptySummaryText
        ? `<p class="meta home-heatmap-summary-empty">${escapeHtml(emptySummaryText)}</p>`
        : "";

  return `
    <div class="${escapeHtml(wrapperClassName)}">
      <div class="home-heatmap-header">
        <div class="home-heatmap-header-copy">
          <p class="home-heatmap-kicker">BODY MAP</p>
          <h3>${escapeHtml(title)}</h3>
          ${leadText ? `<p class="meta home-heatmap-header-lead">${escapeHtml(leadText)}</p>` : ""}
          ${leadEntry && subtitle ? `<p class="small home-heatmap-header-note">${escapeHtml(subtitle)}</p>` : ""}
        </div>
        ${overviewHtml}
      </div>
      <div class="home-heatmap-stage">
        <div class="home-heatmap-figure-pane">
          <p class="home-heatmap-figure-title">전면</p>
          ${renderBodyHeatmapFigure("front", safeBodyHeatmap.front)}
        </div>
        <div class="home-heatmap-figure-pane">
          <p class="home-heatmap-figure-title">후면</p>
          ${renderBodyHeatmapFigure("back", safeBodyHeatmap.back)}
        </div>
      </div>
      <div class="home-heatmap-summary">
        ${summaryHtml}
      </div>
    </div>
  `;
}

export function renderHomeTab({ activeProgram, activeSession, workouts, homePreview }) {
  const previewSourceItems = homePreview?.items || [];
  const previewItems = previewSourceItems
    .slice(0, 3)
    .map(
      (item) => `
      <div class="home-program-preview-item">
        <span class="home-program-preview-index">${item.order + 1}</span>
        <div class="home-program-preview-copy">
          <p class="home-program-preview-name">${escapeHtml(item.name || "운동")}</p>
          <p class="home-program-preview-meta">${escapeHtml(item.primaryMuscle || "부위 정보 없음")}</p>
        </div>
      </div>
    `,
    )
    .join("");
  const displayedCount = Math.min(3, previewSourceItems.length);
  const totalPreviewCount = Math.max(displayedCount, Number(homePreview?.totalItems ?? previewSourceItems.length) || 0);
  const hiddenPreviewCount = Math.max(0, totalPreviewCount - displayedCount);
  const previewMoreHint =
    hiddenPreviewCount > 0
      ? `<p class="small home-program-preview-more">... 외 ${hiddenPreviewCount}개 운동</p>`
      : "";

  const muscleSummary = (homePreview?.muscles || []).length > 0
    ? escapeHtml(homePreview.muscles.join(", "))
    : "-";
  const bodyHeatmapHtml = renderBodyHeatmapCard(homePreview?.bodyHeatmap);

  const activeProgramHtml = activeProgram
    ? `
      <div class="card home-program-card">
        <div class="home-program-card-header">
          <div class="home-program-card-title-block">
            <p class="home-program-card-kicker">TODAY'S ROUTINE</p>
            <h3>${escapeHtml(activeProgram.name)}</h3>
          </div>
          <div class="home-program-card-day-block">
            <span class="home-program-card-day-label">오늘의 DAY</span>
            <p class="home-program-card-day-name">${escapeHtml(homePreview?.dayAlias || "오늘 루틴")}</p>
          </div>
        </div>
        <p class="meta home-program-card-meta">진행 ${activeProgram.trial ?? 0}회 · ${activeProgram.cycle?.lengthDays ?? "-"}일 루틴</p>
        <div class="home-program-summary">
          <span class="home-program-summary-label">오늘 주요 부위</span>
          <p class="small home-program-summary-value">${muscleSummary}</p>
        </div>
        <div class="home-program-preview-list">
          ${previewItems || `<p class="meta home-program-preview-empty">오늘 루틴 미리보기가 없습니다.</p>`}
        </div>
        ${previewMoreHint}
        <div class="home-start-actions">
          <button class="home-start-primary-btn" id="startProgramWorkoutBtn" data-program-id="${renderSafeAttrValue(activeProgram.id)}">오늘 루틴 시작</button>
          <button class="secondary home-start-secondary-btn" id="startProgramWorkoutAltBtn" data-program-id="${renderSafeAttrValue(activeProgram.id)}">다른 루틴 선택하기</button>
        </div>
      </div>
    `
    : `
      <div class="card">
        <h3>진행중 프로그램이 없습니다</h3>
        <p class="meta">프로그램 탭에서 프로그램 검색 또는 새 프로그램 추가를 진행하세요.</p>
        <button id="goProgramsBtn">프로그램 탭으로 이동</button>
      </div>
    `;

  const sessionHtml = activeSession
    ? `
      <div class="card">
        <h3>진행 중 운동</h3>
        <p class="meta">시작 시간: ${formatDateTime(activeSession.startedAt)}</p>
        <button id="resumeWorkoutBtn" data-workout-id="${renderSafeAttrValue(activeSession.workoutId)}">운동 이어하기</button>
      </div>
    `
    : "";

  return `
    ${sessionHtml}
    ${activeProgramHtml}
    ${bodyHeatmapHtml}
    <div class="card">
      <h3>오늘의 운동 추가</h3>
      <p class="meta">프로그램 없이 오늘 운동을 직접 구성해서 시작할 수 있습니다.</p>
      <button class="secondary" id="startManualWorkoutBtn">오늘의 운동 추가하기</button>
      <button class="secondary" id="openManualWorkoutImportModalBtn">지난 운동 불러오기</button>
    </div>
  `;
}

function renderLogSetRow(item, setIndex, set) {
  const weightText =
    item?.weightRequiredSnapshot === false
      ? "체중"
      : typeof set?.weight === "number"
        ? `${set.weight}kg`
        : "-";
  const repsText =
    typeof set?.reps === "number"
      ? String(set.reps)
      : "-";

  return `<p class="log-set-row">Set${setIndex + 1} : ${escapeHtml(weightText)} * ${escapeHtml(repsText)}</p>`;
}

function countLoggedItemSets(item) {
  const setValues = Object.values(item?.sets || {});
  const hasTrackedDoneState = setValues.some((set) => typeof set?.done === "boolean");

  if (!hasTrackedDoneState) {
    return setValues.length;
  }

  return setValues.filter((set) => set?.done).length;
}

function countLoggedWorkoutSets(workout) {
  return Object.values(workout?.items || {}).reduce((total, item) => {
    return total + countLoggedItemSets(item);
  }, 0);
}

function pickBestSet(sets, options = {}) {
  const weightRequired = options.weightRequired !== false;
  const setValues = Array.isArray(sets) ? sets : Object.values(sets || {});

  return setValues.reduce((best, set) => {
    const weight = weightRequired ? Math.max(0, Number(set?.weight || 0)) : 0;
    const reps = Math.max(0, Number(set?.reps || 0));
    if (weight <= 0 && reps <= 0) {
      return best;
    }
    if (!best) {
      return { weight, reps };
    }
    if (weightRequired) {
      if (weight > best.weight || (weight === best.weight && reps > best.reps)) {
        return { weight, reps };
      }
      return best;
    }
    if (reps > best.reps) {
      return { weight, reps };
    }
    return best;
  }, null) || { weight: 0, reps: 0 };
}

function createWorkoutItemHeatmapEntry(item) {
  const fallbackExercise =
    typeof window !== "undefined" && item?.exerciseId
      ? window.exerciseIndex?.[item.exerciseId]
      : null;
  const primaryMuscle = String(
    item?.primaryMuscleSnapshot || fallbackExercise?.primaryMuscle || "",
  ).trim();
  const rawSecondaryMuscles =
    Array.isArray(item?.secondaryMusclesSnapshot) && item.secondaryMusclesSnapshot.length > 0
      ? item.secondaryMusclesSnapshot
      : Array.isArray(fallbackExercise?.secondaryMuscles)
        ? fallbackExercise.secondaryMuscles
        : [];
  const secondaryMuscles = Array.from(
    new Set(
      rawSecondaryMuscles
        .map((muscle) => String(muscle || "").trim())
        .filter((muscle) => muscle && muscle !== primaryMuscle),
    ),
  );

  return {
    primaryMuscle,
    secondaryMuscles,
    sets: countLoggedItemSets(item),
  };
}

function createWorkoutLogHeatmap(workout) {
  const entries = Object.values(workout?.items || {})
    .map((item) => createWorkoutItemHeatmapEntry(item))
    .filter((entry) => entry.sets > 0 && (entry.primaryMuscle || entry.secondaryMuscles.length > 0));

  if (entries.length === 0) {
    return createEmptyBodyHeatmap();
  }

  return createBodyHeatmapFromEntries(entries);
}

function formatWorkoutDuration(workout) {
  let durationStr = "0분";
  if (workout?.meta?.startedAt) {
    const start = new Date(workout.meta.startedAt);
    const endTimestamp = workout.meta.finishedAt ?? workout.meta.endedAt;
    const end = endTimestamp ? new Date(endTimestamp) : new Date();
    const diffMin = Math.max(0, Math.floor((end - start) / 60000));
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    if (h > 0) {
      durationStr = m > 0 ? `${h}시간 ${m}분` : `${h}시간`;
    } else {
      durationStr = `${m}분`;
    }
  }
  return durationStr;
}

function formatActiveSessionElapsed(startedAt) {
  const safeStartedAt = Number(startedAt);
  if (!Number.isFinite(safeStartedAt) || safeStartedAt <= 0) {
    return "00:00:00";
  }

  const totalSec = Math.max(0, Math.floor((Date.now() - safeStartedAt) / 1000));
  const hour = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const minute = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const sec = String(totalSec % 60).padStart(2, "0");
  return `${hour}:${minute}:${sec}`;
}

function renderShareStatsRow(stats) {
  const safeStats = Array.isArray(stats) ? stats.filter(Boolean).slice(0, 3) : [];
  if (safeStats.length <= 0) {
    return "";
  }

  const statsHtml = safeStats
    .map((stat, index) => {
      const valueText = escapeHtml(String(stat?.value ?? "0"));
      const suffixText = String(stat?.suffix || "").trim();
      const labelText = escapeHtml(String(stat?.label || "").trim() || "-");
      const suffixHtml = suffixText ? `<small>${escapeHtml(suffixText)}</small>` : "";
      const dividerHtml = index < safeStats.length - 1 ? `<div class="share-stat-divider"></div>` : "";
      return `
        <div class="share-stat-box">
          <span class="share-stat-val">${valueText}${suffixHtml}</span>
          <span class="share-stat-label">${labelText}</span>
        </div>
        ${dividerHtml}
      `;
    })
    .join("");

  return `
    <div class="share-stats-row">
      ${statsHtml}
    </div>
  `;
}

function renderShareHeatmapSection(heatmapData, options = {}) {
  const safeHeatmapData = heatmapData && typeof heatmapData === "object"
    ? heatmapData
    : createEmptyBodyHeatmap();
  const sectionLabel = escapeHtml(String(options?.label || "오늘의 자극 부위"));
  const frontSvg = renderBodyHeatmapFigure("front", safeHeatmapData.front || []);
  const backSvg = renderBodyHeatmapFigure("back", safeHeatmapData.back || []);
  const topMuscles = (safeHeatmapData.summary || [])
    .slice(0, 3)
    .map(
      (entry) =>
        `<span class="share-muscle-pill" style="${safeRendererStyleDeclaration("--dot-color", getBodyHeatColor(entry.ratio), "--dot-color:#edf2f7")}">${escapeHtml(entry.label)}</span>`,
    )
    .join("");

  return `
    <div class="share-heatmap-section">
      <p class="share-section-label">${sectionLabel}</p>
      <div class="share-heatmap-figures">
        <div class="share-heatmap-figure">
          <p class="share-figure-title">전면</p>
          <div class="share-heatmap-svg-wrap">
            <div class="share-heatmap-svg-frame">
              ${frontSvg}
            </div>
          </div>
        </div>
        <div class="share-heatmap-figure">
          <p class="share-figure-title">후면</p>
          <div class="share-heatmap-svg-wrap">
            <div class="share-heatmap-svg-frame">
              ${backSvg}
            </div>
          </div>
        </div>
      </div>
      ${topMuscles ? `<div class="share-muscle-pills">${topMuscles}</div>` : ""}
    </div>
  `;
}

function renderLogItemDetail(item) {
  const setRows = Object.entries(item?.sets || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([setIndex, set]) => {
      return renderLogSetRow(item, Number(setIndex), set);
    })
    .join("");

  return `
    <div class="log-item-block">
      <div class="log-item-head">
        <h4>${escapeHtml(item?.nameSnapshot || "운동")}</h4>
      </div>
      ${setRows || `<p class="small">기록된 세트가 없습니다.</p>`}
    </div>
  `;
}

function renderWorkoutLogDetail(workout) {
  const items = Object.entries(workout?.items || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, item]) => renderLogItemDetail(item))
    .join("");

  return `
    <div class="log-detail-wrap">
      ${items || `<p class="small">운동 상세가 없습니다.</p>`}
    </div>
  `;
}

function formatLogDurationText(meta) {
  const durationStr = formatWorkoutDuration({ meta });
  return durationStr || "-";
}

function formatBestSetText(bestSet) {
  if (!bestSet) {
    return "기록 없음";
  }
  const reps = Math.max(0, Number(bestSet?.reps || 0));
  const weight = Math.max(0, Number(bestSet?.weight || 0));
  if (bestSet.weightRequired === false) {
    return reps > 0 ? `체중 × ${reps}회` : "기록 없음";
  }
  if (weight > 0 && reps > 0) {
    return `${weight}kg × ${reps}회`;
  }
  if (reps > 0) {
    return `${reps}회`;
  }
  return "기록 없음";
}

function formatCalendarMonthLabel(monthKey) {
  const match = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return monthKey || "-";
  }
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const date = new Date(year, monthIndex, 1);
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
  });
}

function toLocalDateKey(date) {
  const safeDate = date instanceof Date ? date : new Date(date);
  const year = safeDate.getFullYear();
  const month = String(safeDate.getMonth() + 1).padStart(2, "0");
  const day = String(safeDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildCalendarCells(monthKey) {
  const match = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) {
    return [];
  }
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const startDate = new Date(year, monthIndex, 1);
  const startWeekday = startDate.getDay();

  return Array.from({ length: 42 }, (_, index) => {
    const cellDate = new Date(year, monthIndex, index - startWeekday + 1);
    const dateKey = normalizeDateKey(toLocalDateKey(cellDate));
    return {
      date: dateKey,
      day: cellDate.getDate(),
      inMonth: cellDate.getMonth() === monthIndex,
      weekday: cellDate.getDay(),
    };
  });
}

export function renderLogsCalendar({
  calendarMonth,
  selectedDate,
  calendarDates = [],
  logsLoadingMonth = "",
}) {
  const today = normalizeDateKey(toLocalDateKey(new Date()));
  const workoutDateMap = new Map(
    (calendarDates || [])
      .map((entry) => {
        const date = normalizeDateKey(entry?.date);
        if (!date) {
          return null;
        }
        return [date, Math.max(0, Number(entry?.workoutCount || 0))];
      })
      .filter(Boolean),
  );
  const cells = buildCalendarCells(calendarMonth);
  const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
  const workoutDayCount = workoutDateMap.size;
  const totalWorkoutCount = Array.from(workoutDateMap.values()).reduce((sum, count) => sum + count, 0);
  const maxWorkoutCount = Array.from(workoutDateMap.values()).reduce((max, count) => Math.max(max, count), 0);
  const monthStatText =
    logsLoadingMonth === calendarMonth
      ? "운동 기록을 불러오는 중입니다."
      : workoutDayCount > 0
        ? `이번 달 ${workoutDayCount}일 운동 · 총 ${totalWorkoutCount}회`
        : "이번 달 운동 기록이 없습니다.";

  const cellsHtml = cells
    .map((cell) => {
      const workoutCount = workoutDateMap.get(cell.date) || 0;
      const workoutStrength =
        workoutCount > 0 && maxWorkoutCount > 0
          ? workoutCount / maxWorkoutCount
          : 0;
      const classNames = [
        "logs-calendar-day",
        cell.inMonth ? "is-current-month" : "is-muted",
        cell.date === selectedDate ? "is-selected" : "",
        cell.date === today ? "is-today" : "",
        workoutCount > 0 ? "has-workout" : "",
        cell.weekday === 0 ? "is-sunday" : "",
        cell.weekday === 6 ? "is-saturday" : "",
      ];
      const ariaLabel = [
        formatDateWithWeekday(cell.date || "-"),
        workoutCount > 0 ? `운동 ${workoutCount}회` : "운동 기록 없음",
        cell.date === today ? "오늘" : "",
        cell.date === selectedDate ? "선택한 날짜" : "",
      ]
        .filter(Boolean)
        .join(", ");
      return `
        <button
          class="${renderSafeClassList(classNames)}"
          type="button"
          data-role="logs-select-date"
          data-date="${escapeHtml(cell.date)}"
          aria-pressed="${cell.date === selectedDate ? "true" : "false"}"
          aria-label="${escapeHtml(ariaLabel)}"
          style="${safeRendererStyleDeclaration("--logs-calendar-strength", workoutStrength.toFixed(3), "--logs-calendar-strength:0")}"
        >
          <span class="logs-calendar-day-number">${cell.day}</span>
          <span class="logs-calendar-day-dot" aria-hidden="true"></span>
        </button>
      `;
    })
    .join("");

  return `
    <div class="logs-calendar-card">
      <div class="logs-calendar-header">
        <button class="logs-calendar-nav-btn" type="button" data-role="logs-calendar-nav" data-delta="-1" aria-label="이전 달">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <div class="logs-calendar-title-wrap">
          <strong class="logs-calendar-title">${escapeHtml(formatCalendarMonthLabel(calendarMonth))}</strong>
          <span class="${renderSafeClassList(["logs-calendar-month-stat", logsLoadingMonth === calendarMonth ? "is-loading" : ""])}">${escapeHtml(monthStatText)}</span>
        </div>
        <button class="logs-calendar-nav-btn" type="button" data-role="logs-calendar-nav" data-delta="1" aria-label="다음 달">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>
      <div class="logs-calendar-weekdays">
        ${weekdays.map((label) => `<span>${label}</span>`).join("")}
      </div>
      <div class="logs-calendar-grid">
        ${cellsHtml}
      </div>
    </div>
  `;
}

function renderLogSummaryButton(workoutId) {
  return `
    <button
      class="secondary log-card-summary-btn log-card-action-btn open-log-workout-share-btn"
      data-workout-id="${escapeHtml(workoutId)}"
      type="button"
    >
      <svg class="log-card-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="4"></rect>
        <path d="M8 8h8"></path>
        <path d="M8 12h8"></path>
        <path d="M8 16h5"></path>
      </svg>
      <span>기록 요약 보기</span>
    </button>
  `;
}

function renderOpenFullSessionButton(workoutId, date) {
  return `
    <button
      class="secondary log-card-action-btn"
      type="button"
      data-role="logs-open-full-session"
      data-workout-id="${escapeHtml(workoutId)}"
      data-date="${escapeHtml(date || "")}"
    >
      <svg class="log-card-action-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="3"></rect>
        <path d="M7 8h10"></path>
        <path d="M7 12h10"></path>
        <path d="M7 16h6"></path>
      </svg>
      <span>운동 전체 보기</span>
    </button>
  `;
}

function renderSessionPreviewInline(sessionItemsPreview = [], options = {}) {
  const safeItems = Array.isArray(sessionItemsPreview) ? sessionItemsPreview : [];
  const limit = Math.max(1, Number(options?.limit) || 4);
  const visibleItems = safeItems.slice(0, limit);
  const previewHtml = visibleItems
    .map((item) => {
      const name = escapeHtml(item?.nameSnapshot || "운동");
      return item?.matched
        ? `<strong class="logs-session-preview-match">${name}</strong>`
        : `<span>${name}</span>`;
    })
    .join(`<span class="logs-session-preview-sep">·</span>`);

  if (!previewHtml) {
    return `<span class="logs-session-preview-empty">운동 구성 정보 없음</span>`;
  }

  const hiddenCount = Math.max(0, safeItems.length - visibleItems.length);
  return `
    <span class="logs-session-preview-inline">
      ${previewHtml}
      ${hiddenCount > 0 ? `<span class="logs-session-preview-more">· 외 ${hiddenCount}종목</span>` : ""}
    </span>
  `;
}

function renderExerciseSessionContext(session) {
  const itemCount = Math.max(0, Number(session?.sessionItemCount || 0));
  const totalSets = Math.max(0, Number(session?.sessionTotalSets || 0));
  const totalVolume = Math.max(0, Number(session?.sessionTotalVolume || 0));
  const durationText = formatLogDurationText(session?.meta);
  const previewItems = Array.isArray(session?.sessionItemsPreview) ? session.sessionItemsPreview : [];

  return `
    <div class="log-detail-section logs-session-context">
      <p class="log-detail-section-title">이 운동 구성</p>
      <p class="small logs-session-context-meta">총 ${escapeHtml(String(itemCount))}종목 · 총 ${escapeHtml(totalSets.toLocaleString())}세트 · 총 볼륨 ${escapeHtml(totalVolume.toLocaleString())}kg · 운동 시간 ${escapeHtml(durationText)}</p>
      <div class="logs-session-chip-list">
        ${previewItems
      .map((item) => `
            <span class="${renderSafeClassList(["logs-session-chip", item?.matched ? "is-matched" : ""])}">
              ${escapeHtml(item?.nameSnapshot || "운동")}
            </span>
          `)
      .join("") || `<p class="small">운동 구성 정보가 없습니다.</p>`}
      </div>
    </div>
  `;
}

function renderDateLogCard(row, options = {}) {
  const workoutDetailsById = options?.workoutDetailsById || {};
  const expandedWorkoutId = String(options?.expandedWorkoutId || "");
  const logsLoadingWorkoutId = String(options?.logsLoadingWorkoutId || "");
  const isExpanded = expandedWorkoutId === row.workoutId;
  const totalVolume = Math.max(0, Number(row.meta?.totalVolume || 0));
  const metaTotalSets = Number(row.meta?.totalSets);
  const totalSets =
    Number.isFinite(metaTotalSets) && metaTotalSets >= 0
      ? metaTotalSets
      : countLoggedWorkoutSets(workoutDetailsById[row.workoutId]);
  const durationText = formatLogDurationText(row.meta);
  const expandedContentHtml = logsLoadingWorkoutId === row.workoutId
    ? `<p class="small log-loading-text">상세 기록 불러오는 중...</p>`
    : `
        ${renderWorkoutLogDetail(workoutDetailsById[row.workoutId])}
        <div class="log-card-actions-row log-card-actions-row-detail log-card-actions-row-date-detail">
          ${renderLogSummaryButton(row.workoutId)}
        </div>
      `;

  return renderCollapsibleLogCard({
    workoutId: row.workoutId,
    isExpanded,
    logContext: "date",
    titleLines: [
      `<span class="log-card-title">${escapeHtml(formatWorkoutSourceLabel(row.meta?.source))}</span>`,
      `<span class="meta">${escapeHtml(formatDateWithWeekday(row.meta?.date || "-"))}</span>`,
      `<span class="meta">운동 시간: ${escapeHtml(durationText)}</span>`,
      `<span class="meta">총 세트: ${escapeHtml(totalSets.toLocaleString())}세트 · 총 볼륨: ${escapeHtml(totalVolume.toLocaleString())}kg</span>`,
    ],
    expandedContentHtml,
  });
}

function renderExerciseLogItemDetail(item) {
  const setRows = Object.entries(item?.sets || {})
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([setIndex, set]) => renderLogSetRow(item, Number(setIndex), set))
    .join("");

  return `
    <div class="log-item-block">
      <div class="log-item-head">
        <h4>${escapeHtml(item?.nameSnapshot || "운동")}</h4>
        <span class="meta">${escapeHtml(`${Number(item?.order ?? 0) + 1}번 순서`)}</span>
      </div>
      ${setRows || `<p class="small">기록된 세트가 없습니다.</p>`}
    </div>
  `;
}

function renderExerciseLogDetail(session) {
  const items = Array.isArray(session?.items) ? session.items : [];
  return `
    <div class="log-detail-wrap">
      <div class="log-detail-section">
        <p class="log-detail-section-title">검색한 종목 기록</p>
        ${items.map((item) => renderExerciseLogItemDetail(item)).join("") || `<p class="small">해당 종목 기록이 없습니다.</p>`}
      </div>
      ${renderExerciseSessionContext(session)}
      <div class="log-card-actions-row log-card-actions-row-detail">
        ${renderOpenFullSessionButton(session?.workoutId || "", session?.meta?.date || "")}
        ${renderLogSummaryButton(session?.workoutId || "")}
      </div>
    </div>
  `;
}

function renderExerciseLogCard(session, expandedWorkoutId = "") {
  const isExpanded = expandedWorkoutId === session.workoutId;
  const sessionItemCount = Math.max(0, Number(session?.sessionItemCount || 0));
  const sessionTotalSets = Math.max(0, Number(session?.sessionTotalSets || 0));
  const sessionDurationText = formatLogDurationText(session?.meta);
  return renderCollapsibleLogCard({
    workoutId: session.workoutId,
    isExpanded,
    logContext: "exercise",
    titleLines: [
      `<span class="log-card-title">${escapeHtml(formatWorkoutSourceLabel(session.meta?.source))}</span>`,
      `<span class="meta">${escapeHtml(formatDateWithWeekday(session.meta?.date || "-"))}</span>`,
      `<span class="meta">해당 종목 세트: ${escapeHtml(String(session.matchedTotalSets || 0))}세트 · 볼륨: ${escapeHtml(Number(session.matchedVolume || 0).toLocaleString())}kg</span>`,
      `<span class="meta">최고 세트: ${escapeHtml(formatBestSetText(session.bestSet))}${session.matchedItemCount > 1 ? ` · ${escapeHtml(String(session.matchedItemCount))}개 블록` : ""}</span>`,
      `<span class="meta">운동 요약: 총 ${escapeHtml(String(sessionItemCount))}종목 · 총 ${escapeHtml(sessionTotalSets.toLocaleString())}세트 · 운동 시간 ${escapeHtml(sessionDurationText)}</span>`,
      `<span class="meta">운동 구성: ${renderSessionPreviewInline(session?.sessionItemsPreview || [], { limit: 4 })}</span>`,
    ],
    expandedContentHtml: renderExerciseLogDetail(session),
  });
}

function renderLogsDateEmptyState(selectedDate) {
  const dateLabel = escapeHtml(formatDateWithWeekday(selectedDate || "-"));
  return `
    <div class="logs-empty-state" role="status" aria-live="polite">
      <div class="logs-empty-state-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="3"></rect>
          <path d="M16 2v4"></path>
          <path d="M8 2v4"></path>
          <path d="M3 10h18"></path>
        </svg>
      </div>
      <p class="logs-empty-state-title">${dateLabel}</p>
      <p class="logs-empty-state-copy">선택한 날짜에 운동 기록이 없습니다.</p>
      <p class="logs-empty-state-hint">운동한 날짜를 선택하면 운동 기록이 여기에 표시됩니다.</p>
    </div>
  `;
}

export function renderLogsDateListHead(selectedDate = "") {
  return `
    <div>
      <h4>${escapeHtml(formatDateWithWeekday(selectedDate || "-"))}</h4>
      <p class="small">선택한 날짜에 진행한 운동 기록입니다.</p>
    </div>
  `;
}

export function renderLogsDateList(options = {}) {
  const selectedDate = String(options?.selectedDate || "");
  const dateWorkouts = Array.isArray(options?.dateWorkouts) ? options.dateWorkouts : [];
  const logsLoadingDate = String(options?.logsLoadingDate || "");
  const listHtml = dateWorkouts
    .map((row) =>
      renderDateLogCard(row, {
        expandedWorkoutId: options?.dateExpandedWorkoutId || "",
        workoutDetailsById: options?.workoutDetailsById || {},
        logsLoadingWorkoutId: options?.logsLoadingWorkoutId || "",
      }))
    .join("");

  return logsLoadingDate === selectedDate
    ? `<p class="meta">해당 날짜의 운동 기록을 불러오는 중입니다.</p>`
    : listHtml || renderLogsDateEmptyState(selectedDate);
}

export function renderLogsDateMode(options = {}) {
  const selectedDate = String(options?.selectedDate || "");
  const calendarMonth = String(options?.calendarMonth || "");

  return `
    <div
      class="card logs-mode-card"
      id="logs-mode-panel-date"
      role="tabpanel"
      aria-labelledby="logs-mode-tab-date"
      tabindex="0"
      data-role="logs-mode-panel"
      data-logs-mode="date"
    >
      <div data-role="logs-calendar-panel">
        ${renderLogsCalendar({
    calendarMonth,
    selectedDate,
    calendarDates: options?.calendarDates || [],
    logsLoadingMonth: options?.logsLoadingMonth || "",
  })}
      </div>
      <div class="logs-date-list-head" data-role="logs-date-list-head">
        ${renderLogsDateListHead(selectedDate)}
      </div>
      <div class="list" data-role="logs-date-list">
        ${renderLogsDateList(options)}
      </div>
    </div>
  `;
}

export function renderLogsExerciseSearchCard(options = {}) {
  const selectedExerciseName = String(options?.selectedExerciseName || "").trim();

  return `
    <div class="logs-exercise-search-card" data-role="logs-exercise-search-card">
      <div>
        <h4>종목별 기록</h4>
        <p class="small">검색한 운동이 포함된 운동 기록만 모아 보여줍니다.</p>
      </div>
      ${renderSearchActionButton({
        id: "openLogsExerciseSearchModalBtn",
        classNames: ["analytics-chart-change-exercise-btn", "analytics-chart-change-exercise-btn-block", selectedExerciseName ? "is-selected" : ""],
        kicker: "운동 검색",
        value: selectedExerciseName || "운동을 선택하세요",
      })}
    </div>
  `;
}

export function renderLogsExerciseList(options = {}) {
  const exerciseHistory = options?.exerciseHistory || null;
  const sessions = Array.isArray(exerciseHistory?.sessions) ? exerciseHistory.sessions : [];

  return !options?.selectedExerciseId
    ? `<p class="meta">운동을 선택하면 해당 종목이 포함된 운동 기록을 볼 수 있습니다.</p>`
    : options?.logsLoadingExercise === options?.selectedExerciseId
      ? `<p class="meta">종목 기록을 불러오는 중입니다.</p>`
      : sessions.map((session) => renderExerciseLogCard(session, options?.exerciseExpandedWorkoutId || "")).join("") || `<p class="meta">선택한 종목의 완료된 운동 기록이 없습니다.</p>`;
}

export function renderLogsExerciseMode(options = {}) {
  const selectedExerciseName = String(options?.selectedExerciseName || "").trim();

  return `
    <div
      class="card logs-mode-card"
      id="logs-mode-panel-exercise"
      role="tabpanel"
      aria-labelledby="logs-mode-tab-exercise"
      tabindex="0"
      data-role="logs-mode-panel"
      data-logs-mode="exercise"
    >
      ${renderLogsExerciseSearchCard({ ...options, selectedExerciseName })}
      <div class="list" data-role="logs-exercise-list">
        ${renderLogsExerciseList(options)}
      </div>
    </div>
  `;
}

export function renderLogsShell(options = {}) {
  const mode = options?.mode === "exercise" ? "exercise" : "date";
  const dateModeClass = mode === "date" ? "is-active" : "";
  const exerciseModeClass = mode === "exercise" ? "is-active" : "";

  return `
    <div class="logs-shell-bar" data-role="logs-shell-card">
      <div class="logs-mode-toggle" role="tablist" aria-label="운동 기록 보기 모드">
        <button
          class="${renderSafeClassList(["logs-mode-btn", dateModeClass])}"
          id="logs-mode-tab-date"
          type="button"
          role="tab"
          aria-selected="${mode === "date" ? "true" : "false"}"
          aria-controls="logs-mode-panel-date"
          tabindex="${mode === "date" ? "0" : "-1"}"
          data-role="logs-set-mode"
          data-mode="date"
        >날짜별</button>
        <button
          class="${renderSafeClassList(["logs-mode-btn", exerciseModeClass])}"
          id="logs-mode-tab-exercise"
          type="button"
          role="tab"
          aria-selected="${mode === "exercise" ? "true" : "false"}"
          aria-controls="logs-mode-panel-exercise"
          tabindex="${mode === "exercise" ? "0" : "-1"}"
          data-role="logs-set-mode"
          data-mode="exercise"
        >종목별</button>
      </div>
    </div>
  `;
}

export function renderLogsTab(options = {}) {
  const mode = options?.mode === "exercise" ? "exercise" : "date";
  return `
    ${renderLogsShell(options)}
    ${mode === "date" ? renderLogsDateMode(options) : renderLogsExerciseMode(options)}
  `;
}

function renderProgramDayBuilder(day, dayIndex, exerciseNameMap = {}) {
  const daySummary = summarizeProgramDay(day, dayIndex);
  const itemRows = (day.items || [])
    .map((item, itemIndex) => {
      const loadType = item.loadType || "none";
      const repsMode = item.repsMode || "none";
      const rmBaseKo =
        item.rmBase === "squat"
          ? "스쿼트"
          : item.rmBase === "deadlift"
            ? "데드리프트"
            : "벤치";
      const loadSummary =
        loadType === "percent1RM"
          ? `${rmBaseKo} 1RM ${item.percent ? `${item.percent}%` : "% 미설정"}`
          : "중량 규칙 없음";
      const repsSummary =
        repsMode === "fixed"
          ? `고정 ${item.fixedReps || "-"}회`
          : repsMode === "range"
            ? `범위 ${item.rangeMin || "-"}~${item.rangeMax || "-"}회`
            : "반복 규칙 없음";
      const selectedName = String(
        item.exerciseDisplayName || exerciseNameMap[item.exerciseId] || "",
      ).trim();
      const isConfigured = !!selectedName;

      return `
        <div class="${renderSafeClassList(["program-item-card", isConfigured ? "is-configured" : "is-draft"])}">
          <div class="program-item-topline">
            <p class="program-item-kicker">운동 ${itemIndex + 1}</p>
            <span class="${renderSafeClassList(["program-item-status-badge", isConfigured ? "is-configured" : "is-draft"])}">${isConfigured ? "구성됨" : "선택 필요"}</span>
          </div>
          <button
            type="button"
            class="${renderSafeClassList(["program-item-select-trigger", "open-program-item-exercise-search-modal-btn", isConfigured ? "is-selected" : "is-empty"])}"
            data-day-index="${renderSafeIndexAttr(dayIndex)}"
            data-item-index="${renderSafeIndexAttr(itemIndex)}"
          >
            <span class="program-item-select-copy">
              <span class="program-item-select-topline">
                <span class="program-item-select-label">운동 종목</span>
              </span>
              <strong>${escapeHtml(selectedName || "운동 선택하기")}</strong>
              <span class="small">${isConfigured ? "탭해서 검색 창에서 다른 운동으로 변경합니다." : "탭해서 검색 창에서 운동을 선택합니다."}</span>
            </span>
            <span class="program-item-select-action-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="6"></circle>
                <path d="m20 20-4.2-4.2"></path>
              </svg>
            </span>
          </button>

          <div class="program-item-section program-item-basic-section">
            <p class="program-item-section-title">기본 설정</p>
            <div class="program-item-grid-basic">
              <div class="field">
                <label>세트 수</label>
                <input data-builder-item-field="sets" data-day-index="${renderSafeIndexAttr(dayIndex)}" data-item-index="${renderSafeIndexAttr(itemIndex)}" type="number" min="1" max="20" value="${escapeHtml(item.sets || "3")}" />
              </div>
              <div class="field">
                <label>휴식(초)</label>
                <input data-builder-item-field="restSec" data-day-index="${renderSafeIndexAttr(dayIndex)}" data-item-index="${renderSafeIndexAttr(itemIndex)}" type="number" min="${MIN_REST_SEC}" max="${MAX_REST_SEC}" value="${escapeHtml(item.restSec || "90")}" />
              </div>
            </div>
          </div>

          <div class="program-item-section">
            <button
              type="button"
              class="program-item-rule-trigger open-program-item-rule-modal-btn"
              data-rule-type="load"
              data-day-index="${renderSafeIndexAttr(dayIndex)}"
              data-item-index="${renderSafeIndexAttr(itemIndex)}"
            >
              <span class="program-item-rule-trigger-copy">
                <span class="program-item-section-title">중량 규칙</span>
                <span class="small">${escapeHtml(loadSummary)}</span>
              </span>
              <span class="program-item-rule-trigger-action">설정</span>
            </button>
          </div>

          <div class="program-item-section">
            <button
              type="button"
              class="program-item-rule-trigger open-program-item-rule-modal-btn"
              data-rule-type="reps"
              data-day-index="${renderSafeIndexAttr(dayIndex)}"
              data-item-index="${renderSafeIndexAttr(itemIndex)}"
            >
              <span class="program-item-rule-trigger-copy">
                <span class="program-item-section-title">반복 규칙</span>
                <span class="small">${escapeHtml(repsSummary)}</span>
              </span>
              <span class="program-item-rule-trigger-action">설정</span>
            </button>
          </div>

          <div class="row button-row program-item-actions">
            <button class="btn-danger-subtle remove-day-item-btn" data-day-index="${renderSafeIndexAttr(dayIndex)}" data-item-index="${renderSafeIndexAttr(itemIndex)}">운동 삭제</button>
          </div>
        </div>
      `;
    })
    .join("");

  return `
    <div class="card program-day-card">
      <div class="program-day-editor-top">
        <div class="field">
          <label>DAY 이름</label>
          <input data-builder-day-field="alias" data-day-index="${renderSafeIndexAttr(dayIndex)}" value="${escapeHtml(day.alias || "")}" placeholder="Push / Pull / Legs" />
        </div>
      </div>

      <div class="row button-row program-day-import-actions">
        <button class="secondary" id="openProgramDayImportModalBtn">기록 불러오기</button>
      </div>

      ${itemRows
      ? `<div class="program-items-stack">${itemRows}</div>`
      : `
          <div class="program-day-empty-state">
            <p class="program-item-section-title">아직 등록된 운동이 없습니다</p>
            <p class="small">운동 추가 버튼으로 이 DAY의 첫 운동을 만들어 보세요.</p>
          </div>
        `
    }
      <div class="row button-row program-day-actions">
        <button class="add-day-item-btn" data-day-index="${renderSafeIndexAttr(dayIndex)}">운동 추가</button>
      </div>
    </div>
  `;
}

function summarizeProgramDay(day, dayIndex) {
  const alias = String(day?.alias || `Day ${dayIndex + 1}`).trim() || `Day ${dayIndex + 1}`;
  const items = Array.isArray(day?.items) ? day.items : [];
  const configuredItems = items.filter((item) => String(item?.exerciseId || "").trim().length > 0).length;

  return {
    alias,
    totalItems: items.length,
    configuredItems,
    isComplete: configuredItems > 0,
  };
}

function summarizeProgramBuilder(builder) {
  const totalDays = Math.max(1, Number(builder?.lengthDays || builder?.days?.length || 1));
  const days = Array.from({ length: totalDays }, (_, index) => summarizeProgramDay(builder?.days?.[index], index));
  const completedDays = days.filter((day) => day.isComplete).length;
  const totalItems = days.reduce((sum, day) => sum + day.totalItems, 0);
  const configuredItems = days.reduce((sum, day) => sum + day.configuredItems, 0);
  const missingDays = days
    .map((day, index) => (day.isComplete ? null : index))
    .filter((index) => Number.isInteger(index));
  const nameReady = String(builder?.name || "").trim().length > 0;
  const rawLengthDays = String(builder?.lengthDays || "").trim();
  const parsedLengthDays = Number(rawLengthDays);
  const lengthDaysReady =
    rawLengthDays.length > 0 &&
    Number.isInteger(parsedLengthDays) &&
    parsedLengthDays >= 1 &&
    parsedLengthDays <= MAX_PROGRAM_LENGTH_DAYS;
  const infoReady = nameReady && lengthDaysReady;
  const canSave = infoReady && completedDays === totalDays;

  return {
    totalDays,
    days,
    completedDays,
    totalItems,
    configuredItems,
    missingDays,
    nameReady,
    lengthDaysReady,
    infoReady,
    canSave,
    completionPercent: totalDays > 0 ? Math.round((completedDays / totalDays) * 100) : 0,
  };
}

function renderProgramBuilderSummary(summary, missingDayLabels) {
  return `
    <div class="program-builder-stat-grid">
      <div class="program-builder-stat">
        <strong>${summary.totalDays}</strong>
        <span>총 DAY</span>
      </div>
      <div class="program-builder-stat">
        <strong>${summary.completedDays}</strong>
        <span>구성 완료</span>
      </div>
      <div class="program-builder-stat">
        <strong>${summary.configuredItems}</strong>
        <span>선택된 운동</span>
      </div>
      <div class="program-builder-stat">
        <strong>${summary.totalItems}</strong>
        <span>입력 슬롯</span>
      </div>
    </div>
    <div class="program-builder-checklist">
      <div class="${renderSafeClassList(["program-builder-check", summary.nameReady ? "is-complete" : "is-pending"])}">
        <strong>${summary.nameReady ? "프로그램명 입력 완료" : "프로그램명 입력 필요"}</strong>
        <span>${summary.nameReady ? "프로그램 이름이 저장 조건을 만족합니다." : "프로그램 목록에서 바로 구분할 수 있는 이름을 입력하세요."}</span>
      </div>
      <div class="${renderSafeClassList(["program-builder-check", summary.completedDays === summary.totalDays ? "is-complete" : "is-pending"])}">
        <strong>${summary.completedDays === summary.totalDays ? "모든 DAY 구성이 완료되었습니다" : `${summary.completedDays}/${summary.totalDays} DAY 완료`}</strong>
        <span>${missingDayLabels.length > 0 ? `비어 있는 DAY: ${escapeHtml(missingDayLabels.join(", "))}` : "모든 DAY가 최소 1개 운동을 포함합니다."}</span>
      </div>
    </div>
  `;
}

function renderProgramBuilderBackRow(targetClass, label = "이전") {
  return `
    <div class="program-builder-back-row">
      <button class="${renderSafeClassList(["secondary", targetClass])}" type="button">${label}</button>
    </div>
  `;
}

function renderProgramBuilderTopNav(stage, isEditing, summary, safeDayIndex) {
  const isInfo = stage === "info";
  const backArrowSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>`;
  const backClass = isInfo ? "back-to-program-list-btn" : "back-to-program-info-btn";
  const backLabel = isInfo ? "목록으로" : "기본 정보로";
  const fillPct = isInfo ? 50 : 100;
  const stepNum = isInfo ? 1 : 2;
  const stepName = isInfo ? "기본 정보" : "DAY 구성";

  return `
    <div class="program-builder-topnav program-builder-topnav-unified">
      <button class="${renderSafeClassList(["program-builder-nav-btn", "program-builder-back-icon-btn", backClass])}" type="button" aria-label="${backLabel}">
        ${backArrowSvg}
      </button>
      <div class="program-builder-progress-wrap">
        <div class="program-builder-progress-track">
          <div class="program-builder-progress-fill" style="${safeRendererPercentStyle("width", fillPct, "width:0%")}"></div>
        </div>
        <span class="program-builder-progress-label">
          <span class="program-builder-progress-step">${stepNum} / 2</span>
          <span class="program-builder-progress-name">${stepName}</span>
        </span>
      </div>
    </div>
  `;
}

function renderProgramDaySubNav(safeDayIndex, totalDays) {
  const prevSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 19l-7-7 7-7"/></svg>`;
  const nextSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 19l7-7-7-7"/></svg>`;
  const caretSvg = `<svg class="program-day-title-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>`;
  return `
    <div class="program-builder-day-subnav">
      <button class="program-builder-day-subnav-btn" id="programDayPrevBtn" type="button" aria-label="이전 DAY" ${safeDayIndex === 0 ? "disabled" : ""}>
        ${prevSvg}
      </button>
      <button class="program-builder-day-subnav-btn program-builder-day-subnav-title open-program-builder-day-picker-btn" type="button" aria-label="DAY 선택">
        <span class="program-day-title-copy">
          <span class="program-day-title-kicker">DAY</span>
          <span class="program-day-title-value">${safeDayIndex + 1} / ${totalDays}</span>
        </span>
        ${caretSvg}
      </button>
      <button class="program-builder-day-subnav-btn" id="programDayNextBtn" type="button" aria-label="다음 DAY" ${safeDayIndex === Math.max(0, totalDays - 1) ? "disabled" : ""}>
        ${nextSvg}
      </button>
    </div>
  `;
}

function renderProgramBuilderDaySaveNote(summary, missingDayLabels) {
  if (summary?.infoReady === false) {
    return `
      <div class="program-builder-note is-warning">
        <p>프로그램명과 루틴 길이를 먼저 확인해 주세요.</p>
      </div>
    `;
  }

  if (Array.isArray(missingDayLabels) && missingDayLabels.length > 0) {
    return `
      <div class="program-builder-note is-warning">
        <p>저장하려면 ${escapeHtml(missingDayLabels.join(", "))}에 운동을 추가해 주세요.</p>
      </div>
    `;
  }

  return "";
}

export function renderProgramTemplateSearchResults(templates = [], templateQuery = "") {
  const normalizedTemplateQuery = String(templateQuery || "").trim().toLowerCase();
  const filteredTemplates = (templates || []).filter((template) => {
    if (!normalizedTemplateQuery) {
      return true;
    }
    const haystack = `${template?.name || ""} ${template?.summary || ""} ${(template?.tags || []).join(" ")}`.toLowerCase();
    return haystack.includes(normalizedTemplateQuery);
  });

  if (filteredTemplates.length <= 0) {
    return `<p class="meta">${normalizedTemplateQuery ? "검색 결과가 없습니다." : "사용 가능한 템플릿이 없습니다."}</p>`;
  }

  return `
    <div class="list">
      ${filteredTemplates
      .map(
        (template) => `
          <div class="list-item">
            <h4>${escapeHtml(template.name)}</h4>
            <p class="meta">${escapeHtml(template.summary || "-")}</p>
            <p class="small">length ${template.lengthDays}일 · tags: ${escapeHtml((template.tags || []).join(", "))}</p>
            <div class="row button-row" style="margin-top:8px">
              <button class="secondary select-program-template-btn" data-template-id="${escapeHtml(template.id)}">이 템플릿 추가</button>
            </div>
          </div>
        `,
      )
      .join("")}
    </div>
  `;
}

export function renderProgramsTab(
  programs,
  builder,
  dayIndex = 0,
  stage = "list",
  templates = [],
  templateQuery = "",
  exerciseNameMap = {},
) {
  const isEditing = !!builder?.targetProgramId;
  const builderSummary = summarizeProgramBuilder(builder);
  const totalDays = builderSummary.totalDays;
  const safeDayIndex = Math.min(Math.max(0, Number(dayIndex) || 0), totalDays - 1);
  const selectedDay = builder?.days?.[safeDayIndex] || {
    alias: `Day ${safeDayIndex + 1}`,
    items: [],
  };
  const missingDayLabels = builderSummary.missingDays.map((index) => `DAY ${index + 1}`);
  void templates;
  void templateQuery;

  const listHtml = programs
    .map(
      (program) => `
        <div class="${renderSafeClassList(["list-item", "program-list-item", program.isActive ? "is-active" : ""])}">
          <h4>${escapeHtml(program.name)}</h4>
          ${program.isActive ? `<p class="small active-program-label">현재 활성 프로그램</p>` : ""}
          <p class="meta">루틴 길이 ${program.cycle?.lengthDays || 0}일 · 진행 횟수 ${program.trial || 0}</p>
          <p class="small">최근 완료: ${program.lastCompleted
          ? `Day ${Number(program.lastCompleted.dayIndex) + 1} · ${formatDateTime(program.lastCompleted.completedAt)}`
          : "아직 수행 이력 없음"
        }</p>
          <p class="small">최근 수정: ${formatDateTime(program.updatedAt)}</p>
          <div class="row button-row" style="margin-top:8px">
            <button
              class="${renderSafeClassList([program.isActive ? "active-program-btn" : "secondary", "set-active-program-btn"])}"
              data-program-id="${renderSafeAttrValue(program.id)}"
              data-is-active="${renderSafeAttrValue(program.isActive ? "true" : "false", "false")}"
            >
              활성화
            </button>
            <button class="secondary load-program-to-builder-btn" data-program-id="${renderSafeAttrValue(program.id)}">편집</button>
            <button class="danger delete-program-btn" data-program-id="${renderSafeAttrValue(program.id)}">삭제</button>
          </div>
        </div>
      `,
    )
    .join("");

  const listScreen = `
    <div class="card">
      <h3>프로그램</h3>
      <p class="meta">목록에서 관리하고, 템플릿 검색으로 바로 내 프로그램에 추가할 수 있습니다.</p>
      <div class="row button-row" style="margin-top:12px; margin-bottom:12px">
        <button class="primary btn-elevated program-create-btn" id="openProgramBuilderBtn">프로그램 추가하기</button>
      </div>
      ${renderSearchActionButton({
        id: "openProgramTemplateSearchBtn",
        classNames: ["analytics-chart-change-exercise-btn", "analytics-chart-change-exercise-btn-block", "program-template-search-trigger", "secondary"],
        kicker: "프로그램 검색",
        value: "템플릿 검색하기",
      })}
    </div>

    <div class="card">
      <h3>등록된 프로그램</h3>
      <div class="list">${listHtml || `<p class="meta">프로그램이 없습니다.</p>`}</div>
    </div>
  `;

  if (stage === "list") {
    return listScreen;
  }

  if (stage === "search") {
    return listScreen;
  }

  const infoScreen = `
    <div class="program-builder-screen">

      ${renderProgramBuilderTopNav("info", isEditing, builderSummary, safeDayIndex)}
      <div class="program-builder-layout">
        <div class="card program-builder-form-card">
          <div class="program-builder-section-head">
            <div>
              <h3>기본 정보</h3>
              <p class="small">${isEditing ? "기존 프로그램을 불러와 수정 중입니다." : "먼저 프로그램의 이름과 길이를 정합니다."}</p>
            </div>
          </div>
          <div class="field">
            <label>프로그램명</label>
            <input data-builder-root="name" value="${escapeHtml(builder?.name || "")}" placeholder="예: 4-Day Hybrid" />
          </div>
          <div class="field">
            <label>루틴 길이(일)</label>
            <input
              data-builder-root="lengthDays"
              type="number"
              min="1"
              max="${MAX_PROGRAM_LENGTH_DAYS}"
              value="${escapeHtml(builder?.lengthDays || "4")}"
            />
          </div>
          <div class="field">
            <label>활성화</label>
            <label class="checkbox-label"><input data-builder-root="isActive" type="checkbox" ${builder?.isActive ? "checked" : ""} /><span class="checkbox-text">생성과 동시에 활성화</span></label>
          </div>
        </div>
      </div>
      <p class="error" id="programError"></p>
      <div class="row button-row">
        <button
          class="primary"
          id="programBuilderGoDayBtn"
          ${builderSummary.infoReady ? "" : 'disabled title="프로그램명과 루틴 길이를 먼저 입력하세요."'}
        >
          DAY 구성하기
        </button>
      </div>
    </div>
  `;

  if (stage === "info") {
    return infoScreen;
  }

  return `
    <div class="program-builder-screen">

      ${renderProgramBuilderTopNav("day", isEditing, builderSummary, safeDayIndex)}
      ${renderProgramDaySubNav(safeDayIndex, builderSummary.totalDays)}
      <div class="program-day-layout">
        ${renderProgramDayBuilder(selectedDay, safeDayIndex, exerciseNameMap)}
        ${renderProgramBuilderDaySaveNote(builderSummary, missingDayLabels)}

        <p class="error" id="programError"></p>
        
        <div class="row button-row">
          <button class="primary" id="createProgramFromBuilderBtn" ${builderSummary.canSave ? "" : 'disabled title="모든 DAY에 최소 1개 운동이 필요합니다."'}>
            ${isEditing ? "수정 저장" : "프로그램 저장"}
          </button>
        </div>
      </div>
    </div>
  `;
}

export function renderAnalyticsTab({ query, searchItems, selectedExerciseId, selectedExerciseName, volumeSeries, estimatedOneRM }) {
  const selectedFromResult =
    (searchItems || []).find((item) => String(item.id || "") === String(selectedExerciseId || "")) || null;
  const selectedLabel = String(
    selectedExerciseName ||
    selectedFromResult?.nameKo ||
    selectedFromResult?.nameEn ||
    "",
  ).trim();
  const big3Config = [
    { key: "squat", label: "Squat" },
    { key: "bench", label: "Bench Press" },
    { key: "deadlift", label: "Deadlift" },
  ];

  const big3Entries = big3Config.map(({ key, label }) => {
    const entry = estimatedOneRM?.[key] || null;
    const value = Math.round(Number(entry?.value || 0));
    const hasValue = Number.isFinite(value) && value > 0;
    const weight = Number(entry?.weight || 0);
    const reps = Number(entry?.reps || 0);
    return {
      key,
      label,
      hasValue,
      value: hasValue ? value : 0,
      weight: Number.isFinite(weight) && weight > 0 ? weight : 0,
      reps: Number.isFinite(reps) && reps > 0 ? Math.round(reps) : 0,
      date: String(entry?.date || ""),
    };
  });
  const availableBig3 = big3Entries.filter((entry) => entry.hasValue);
  const big3Total = availableBig3.reduce((sum, entry) => sum + entry.value, 0);
  const big3Html = big3Entries
    .map((entry) => `
      <div class="${renderSafeClassList(["analytics-big3-item", entry.hasValue ? "is-ready" : ""])}">
        <p class="analytics-big3-label">${escapeHtml(entry.label)}</p>
        <p class="analytics-big3-value">${entry.hasValue ? `${entry.value.toLocaleString()} kg` : "-"}</p>
        <p class="small analytics-big3-meta">${entry.hasValue ? `${entry.weight}kg x ${entry.reps}회 (${escapeHtml(entry.date)})` : "기록 없음"}</p>
      </div>
    `)
    .join("");
  const big3TotalText = availableBig3.length > 0
    ? `${big3Total.toLocaleString()} kg`
    : "-";

  const groupedSeries = (volumeSeries?.points || []).reduce((acc, point) => {
    const date = normalizeDateKey(point?.date);
    if (!date) {
      return acc;
    }
    acc[date] = (acc[date] || 0) + Math.max(0, Number(point?.volume || 0));
    return acc;
  }, {});

  const seriesHtml = Object.entries(groupedSeries)
    .map(([date, volume]) => ({
      date,
      volume: Number(volume || 0),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const chartHtml = renderVolumeLineChart(seriesHtml);

  const exerciseBtnClass = selectedLabel ? "is-selected" : "";

  return `
    <div class="card">
      <h3>삼대 추정 1RM</h3>
      <p class="small">공식: 1RM = W x (1 + 0.0333 x R)</p>
      <div class="analytics-big3-grid">
        ${big3Html}
      </div>
      <p class="analytics-big3-total">합계: <strong>${big3TotalText}</strong></p>
    </div>

    <div class="card">
      <div class="analytics-chart-header">
        <div>
          <h3>볼륨 변화 그래프</h3>
          <p class="small analytics-chart-header-copy">분석할 운동을 검색해서 그래프를 확인하세요.</p>
        </div>
      </div>
      ${renderSearchActionButton({
        id: "openAnalyticsSearchModalBtn",
        classNames: ["analytics-chart-change-exercise-btn", "analytics-chart-change-exercise-btn-block", exerciseBtnClass],
        kicker: "운동 검색",
        value: selectedLabel ? selectedLabel : "운동을 선택하세요",
      })}
      ${chartHtml}
    </div>
  `;
}

function renderVolumeLineChart(points) {
  if (!Array.isArray(points) || points.length === 0) {
    return `<p class="meta">운동을 선택하면 데이터가 표시됩니다.</p>`;
  }

  const width = 760;
  const height = 340;
  const paddingLeft = 66;
  const paddingRight = 36;
  const paddingTop = 20;
  const paddingBottom = 52;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;
  const baselineY = height - paddingBottom;

  const volumes = points.map((point) => Number(point.volume) || 0);
  const rawMin = Math.min(...volumes);
  const rawMax = Math.max(...volumes);
  const minVolume = rawMin === rawMax ? rawMin - 1 : rawMin;
  const maxVolume = rawMax === minVolume ? minVolume + 1 : rawMax;
  const range = maxVolume - minVolume;

  const toX = (index) => {
    if (points.length === 1) {
      return paddingLeft + chartWidth / 2;
    }
    return paddingLeft + (chartWidth * index) / (points.length - 1);
  };
  const toY = (volume) => {
    const ratio = (volume - minVolume) / range;
    return paddingTop + chartHeight - ratio * chartHeight;
  };

  // Y-axis gridlines (3 levels: bottom, mid, top)
  const gridLevels = [0, 0.5, 1];
  const gridLines = gridLevels.map((ratio) => {
    const vol = minVolume + range * ratio;
    const y = toY(vol);
    const label = `${Math.round(vol).toLocaleString()} kg`;
    return `
      <line class="analytics-chart-gridline" x1="${paddingLeft}" y1="${y.toFixed(2)}" x2="${paddingLeft + chartWidth}" y2="${y.toFixed(2)}" />
      <text class="analytics-chart-gridlabel" x="${(paddingLeft - 6).toFixed(2)}" y="${(y + 3.5).toFixed(2)}" text-anchor="end">${label}</text>
    `;
  }).join("");

  const pointCoords = points.map((point, index) => ({
    x: toX(index),
    y: toY(point.volume),
  }));

  const linePath = pointCoords
    .map((coord, index) => `${index === 0 ? "M" : "L"} ${coord.x.toFixed(2)} ${coord.y.toFixed(2)}`)
    .join(" ");
  const firstPoint = pointCoords[0];
  const lastPoint = pointCoords[pointCoords.length - 1];
  const areaPath = `${linePath} L ${lastPoint.x.toFixed(2)} ${baselineY.toFixed(2)} L ${firstPoint.x.toFixed(2)} ${baselineY.toFixed(2)} Z`;

  const hitAreaSize = 22;
  const dots = points
    .map(
      (point, index) => `
        <rect
          class="analytics-chart-hit-area"
          x="${(pointCoords[index].x - hitAreaSize).toFixed(2)}"
          y="${(pointCoords[index].y - hitAreaSize).toFixed(2)}"
          width="${(hitAreaSize * 2).toFixed(2)}"
          height="${(hitAreaSize * 2).toFixed(2)}"
          rx="4"
          data-chart-tooltip-date="${escapeHtml(point.date)}"
          data-chart-tooltip-value="${renderSafeAttrValue(Number(point.volume || 0).toLocaleString() + ' kg')}"
          data-chart-tooltip-cx="${renderSafeAttrValue(pointCoords[index].x.toFixed(2))}"
          data-chart-tooltip-cy="${renderSafeAttrValue(pointCoords[index].y.toFixed(2))}"
        />
        <circle
          class="analytics-chart-dot"
          cx="${pointCoords[index].x.toFixed(2)}"
          cy="${pointCoords[index].y.toFixed(2)}"
          r="4.5"
          pointer-events="none"
        />
      `,
    )
    .join("");

  const xIndexes = Array.from(new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]))
    .filter((index) => index >= 0 && index < points.length);
  const xLabels = xIndexes
    .map((index) => {
      const point = points[index];
      const label = point.date.length >= 5 ? point.date.slice(5) : point.date;
      return `
        <text
          class="analytics-chart-x-label"
          x="${toX(index).toFixed(2)}"
          y="${(height - 14).toFixed(2)}"
          text-anchor="middle"
        >
          ${escapeHtml(label)}
        </text>
      `;
    })
    .join("");

  const latest = points[points.length - 1];
  const previous = points.length > 1 ? points[points.length - 2] : null;
  const deltaValue = previous ? Number(latest.volume || 0) - Number(previous.volume || 0) : 0;
  const deltaSign = deltaValue > 0 ? "+" : deltaValue < 0 ? "-" : "";
  const deltaPercent =
    previous && Number(previous.volume || 0) > 0
      ? (Math.abs(deltaValue) / Number(previous.volume || 0)) * 100
      : null;
  const trendClass = deltaValue > 0 ? "is-up" : deltaValue < 0 ? "is-down" : "is-flat";
  const trendText = previous
    ? `${deltaSign}${Math.abs(deltaValue).toLocaleString()}${deltaPercent !== null ? ` (${deltaSign}${deltaPercent.toFixed(1)}%)` : ""}`
    : "기준 데이터 없음";
  const highVolume = Math.max(...volumes);
  const lowVolume = Math.min(...volumes);

  return `
    <div class="analytics-chart-wrap">
      <div class="analytics-chart-meta">
        <p class="${renderSafeClassList(["analytics-chart-change", trendClass])}">최근 변화 ${trendText} kg</p>
        <p class="small">최고 ${highVolume.toLocaleString()} kg</p>
        <p class="small">최저 ${lowVolume.toLocaleString()} kg</p>
      </div>
      <div class="analytics-chart-scroll" style="position:relative">
        <svg class="analytics-chart-svg" viewBox="0 0 ${width} ${height}" role="img" aria-label="운동 볼륨 변화 꺾은선 그래프">
          <defs>
            <linearGradient id="analyticsVolumeAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#0D9488" stop-opacity="0.30" />
              <stop offset="100%" stop-color="#0D9488" stop-opacity="0.03" />
            </linearGradient>
          </defs>
          ${gridLines}
          <line class="analytics-chart-baseline" x1="${paddingLeft}" y1="${baselineY}" x2="${paddingLeft + chartWidth}" y2="${baselineY}" />
          <path class="analytics-chart-area" d="${areaPath}" />
          <path class="analytics-chart-line" d="${linePath}" />
          ${dots}
          ${xLabels}
        </svg>
        <div class="analytics-chart-tooltip" id="analyticsChartTooltip"></div>
      </div>
      <p class="small analytics-chart-latest">최근 ${escapeHtml(latest.date)} · 볼륨 ${Number(latest.volume || 0).toLocaleString()} kg</p>
    </div>
  `;
}

function resolveProfileEnvironmentToneClass(tone = "") {
  if (tone === "ready" || tone === "active") {
    return "is-positive";
  }
  if (tone === "warning") {
    return "is-warning";
  }
  if (tone === "blocked" || tone === "disabled") {
    return "is-danger";
  }
  return "is-neutral";
}

function shouldShowWakeLockEnvironmentNotice(wakeLockStatus = {}) {
  return String(wakeLockStatus?.label || "").trim() === "미지원";
}

function shouldShowRestNotificationEnvironmentNotice(notificationStatus = {}) {
  const tone = String(notificationStatus?.tone || "").trim();
  return tone === "warning" || tone === "blocked";
}

function renderProfileEnvironmentNotice({
  label = "",
  title = "",
  detail = "",
  tone = "",
  actionButton = "",
}) {
  const noticeClasses = ["profile-environment-notice", resolveProfileEnvironmentToneClass(tone)];
  return `
    <div class="${renderSafeClassList(noticeClasses)}">
      <div class="profile-environment-notice-copy">
        ${label ? `<span class="profile-environment-notice-label">${escapeHtml(label)}</span>` : ""}
        <strong>${escapeHtml(title)}</strong>
        ${detail ? `<p class="small profile-environment-notice-detail">${escapeHtml(detail)}</p>` : ""}
      </div>
      ${actionButton ? `<div class="profile-environment-notice-action">${actionButton}</div>` : ""}
    </div>
  `;
}

function renderProfileSectionHeading(kicker, title, copy = "") {
  return `
    <div class="profile-section-head">
      <div>
        <p class="profile-kicker">${escapeHtml(kicker)}</p>
        <h3>${escapeHtml(title)}</h3>
      </div>
      ${copy ? `<p class="small profile-card-copy">${escapeHtml(copy)}</p>` : ""}
    </div>
  `;
}

function renderProfileExpandChevron() {
  return `
    <span class="log-card-chevron profile-summary-chevron" aria-hidden="true">
      <span class="log-card-chevron-text">
        <span class="profile-chevron-label-closed">펼치기</span>
        <span class="profile-chevron-label-open">접기</span>
      </span>
      <svg class="log-card-chevron-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M6 9l6 6 6-6"></path>
      </svg>
    </span>
  `;
}

function resolveRestNotificationPresentation(notificationCapabilities = null) {
  const usesNativeRestAlarm =
    notificationCapabilities?.usesNativeAlarm === true ||
    notificationCapabilities?.platform === "android_twa";

  if (usesNativeRestAlarm) {
    return {
      chipLabel: "휴식 알림",
      sectionCopy: "",
      settingsTitle: "휴식 알림",
      settingsCopy: "알림을 켜고 끌 수 있습니다.",
      supportNote: "",
      permissionButtonLabel: "알림 허용",
      settingsActionButtonLabel: "기기 설정",
    };
  }

  return {
    chipLabel: "휴식 알림",
    sectionCopy: "",
    settingsTitle: "휴식 알림",
    settingsCopy: "알림을 켜고 끌 수 있습니다.",
    supportNote: "",
    permissionButtonLabel: "알림 허용",
  };
}

export function renderProfileTab({
  profile,
  oneRM,
  customExercises = [],
  accountEmail = "",
  settings = {},
  wakeLockStatus = null,
  notificationStatus = null,
  notificationCapabilities = null,
}) {
  const safeWakeLockStatus = wakeLockStatus || {
    label: "확인 필요",
    tone: "idle",
  };
  const safeNotificationStatus = notificationStatus || {
    label: "확인 필요",
    tone: "idle",
    action: "",
    actionLabel: "",
    actionMode: "",
    detail: "",
  };
  const restNotificationPresentation = resolveRestNotificationPresentation(notificationCapabilities);
  let notificationActionButton = "";
  if (safeNotificationStatus.action === "permission") {
    notificationActionButton = `<button class="secondary" id="setupRestNotificationsBtn">${escapeHtml(restNotificationPresentation.permissionButtonLabel)}</button>`;
  }
  if (safeNotificationStatus.action === "exact-alarm") {
    notificationActionButton = `<button class="secondary" id="setupRestNotificationsBtn">${escapeHtml(restNotificationPresentation.settingsActionButtonLabel)}</button>`;
  }
  if (safeNotificationStatus.action === "install") {
    notificationActionButton = `<button class="secondary" id="openRestInstallGuideBtn" data-guide-mode="${escapeHtml(safeNotificationStatus.actionMode || "browser-guide")}">설치하기</button>`;
  }
  const environmentNotices = [];
  if (shouldShowWakeLockEnvironmentNotice(safeWakeLockStatus)) {
    environmentNotices.push(renderProfileEnvironmentNotice({
      label: "화면 유지",
      title: "화면 유지 기능 미지원",
      detail: "이 기기에서는 화면 유지 기능을 사용할 수 없습니다.",
      tone: safeWakeLockStatus.tone,
    }));
  }
  if (shouldShowRestNotificationEnvironmentNotice(safeNotificationStatus)) {
    environmentNotices.push(renderProfileEnvironmentNotice({
      label: restNotificationPresentation.chipLabel,
      title: safeNotificationStatus.label,
      detail: safeNotificationStatus.detail,
      tone: safeNotificationStatus.tone,
      actionButton: notificationActionButton,
    }));
  }
  const environmentNoticeHtml = environmentNotices.join("");
  const customExerciseCount = Array.isArray(customExercises) ? customExercises.length : 0;
  const customSearchHtml = customExerciseCount > 0
    ? `
        <div class="field profile-inline-search-field">
          <label for="profileCustomExerciseSearchInput">운동 검색</label>
          <input id="profileCustomExerciseSearchInput" type="search" placeholder="이름, 부위, 별칭 검색" />
        </div>
      `
    : "";

  const customList = customExercises
    .map(
      (exercise) => {
        const primaryLabel = formatMuscleLabel(exercise.primaryMuscle) || "-";
        const secondaryLabels = Array.from(
          new Set(
            (exercise.secondaryMuscles || [])
              .map((muscle) => formatMuscleLabel(muscle))
              .filter(Boolean),
          ),
        );
        const muscleSummary = secondaryLabels.length > 0
          ? `${primaryLabel} · 보조 ${secondaryLabels.join(", ")}`
          : primaryLabel;
        const aliasText = Array.isArray(exercise.aliases) ? exercise.aliases.filter(Boolean).join(", ") : "";
        const searchText = [
          exercise.nameKo,
          exercise.nameEn,
          primaryLabel,
          secondaryLabels.join(" "),
          aliasText,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return `
          <div class="list-item profile-custom-exercise-item" data-role="profile-custom-exercise-item" data-search-text="${escapeHtml(searchText)}">
            <div class="profile-custom-exercise-head">
              <div>
                <h4>${escapeHtml(exercise.nameKo)}</h4>
                ${exercise.nameEn ? `<p class="small profile-custom-exercise-subtitle">${escapeHtml(exercise.nameEn)}</p>` : ""}
              </div>
              <span class="profile-inline-chip">${escapeHtml(primaryLabel)}</span>
            </div>
            <p class="meta profile-custom-exercise-meta">${escapeHtml(muscleSummary)}</p>
            ${aliasText ? `<p class="small profile-custom-exercise-alias">별칭 · ${escapeHtml(aliasText)}</p>` : ""}
          </div>
        `;
      },
    )
    .join("");

  return `
    <div class="card profile-summary-card">
      <div class="profile-summary-head">
        <div class="profile-summary-copy">
          <p class="profile-kicker">PROFILE</p>
          <h3>${escapeHtml(profile?.nickname || "사용자")}</h3>
          <p class="small profile-summary-meta">${escapeHtml(accountEmail || "-")}</p>
          <p class="small profile-summary-meta">계정 생성 · ${formatDateTime(profile?.createdAt)}</p>
        </div>
      </div>
    </div>

    <div class="card profile-support-card">
      ${renderProfileSectionHeading("WORKOUT ENVIRONMENT", "운동 환경", restNotificationPresentation.sectionCopy)}
      ${environmentNoticeHtml ? `<div class="profile-environment-notice-list">${environmentNoticeHtml}</div>` : ""}
      <div class="profile-settings-list">
        <label class="profile-settings-row" for="settingsAlarmEnabled">
          <span>
            <strong>휴식 알람 소리</strong>
            <small>포그라운드 휴식 종료 시 소리를 재생합니다.</small>
          </span>
          <input id="settingsAlarmEnabled" type="checkbox" ${settings?.alarmEnabled !== false ? "checked" : ""} />
        </label>
        <label class="profile-settings-row" for="settingsVibrationEnabled">
          <span>
            <strong>휴식 진동</strong>
            <small>지원 기기에서 진동으로 휴식 종료를 알립니다.</small>
          </span>
          <input id="settingsVibrationEnabled" type="checkbox" ${settings?.vibrationEnabled !== false ? "checked" : ""} />
        </label>
        <label class="profile-settings-row" for="settingsRestWakeLockEnabled">
          <span>
            <strong>운동 중 화면 유지</strong>
            <small>세션 화면에서 화면 꺼짐을 최대한 막습니다.</small>
          </span>
          <input id="settingsRestWakeLockEnabled" type="checkbox" ${settings?.restWakeLockEnabled !== false ? "checked" : ""} />
        </label>
        <label class="profile-settings-row" for="settingsRestNotificationsEnabled">
          <span>
            <strong>${escapeHtml(restNotificationPresentation.settingsTitle)}</strong>
            <small>${escapeHtml(restNotificationPresentation.settingsCopy)}</small>
          </span>
          <input id="settingsRestNotificationsEnabled" type="checkbox" ${settings?.restNotificationsEnabled !== false ? "checked" : ""} />
        </label>
      </div>
      <div class="row button-row profile-action-row">
        <button id="saveRestSettingsBtn">설정 저장</button>
      </div>
    </div>

    <div class="card profile-rm-card">
      ${renderProfileSectionHeading("TRAINING BASELINE", "1RM", "3대 운동 기준 중량을 한눈에 관리합니다.")}
      <div class="profile-rm-grid">
        <div class="field">
          <label for="squatInput">Squat (kg)</label>
          <input id="squatInput" type="number" min="0" step="0.5" value="${Number(oneRM?.squat || 0)}" />
        </div>
        <div class="field">
          <label for="benchInput">Bench (kg)</label>
          <input id="benchInput" type="number" min="0" step="0.5" value="${Number(oneRM?.bench || 0)}" />
        </div>
        <div class="field">
          <label for="deadliftInput">Deadlift (kg)</label>
          <input id="deadliftInput" type="number" min="0" step="0.5" value="${Number(oneRM?.deadlift || 0)}" />
        </div>
      </div>
      <div class="row button-row profile-action-row">
        <button id="saveOneRMBtn">저장</button>
      </div>
    </div>

    <div class="card profile-custom-list-card">
      <div class="profile-section-head profile-section-head-inline">
        <div>
          <p class="profile-kicker">CUSTOM EXERCISES</p>
          <h3>내 커스텀 운동</h3>
        </div>
        <span class="profile-inline-chip" id="profileCustomExerciseCount">${escapeHtml(`총 ${customExerciseCount}개`)}</span>
      </div>
      ${customSearchHtml}
      <div class="list profile-custom-exercise-list" id="profileCustomExerciseList">
        ${customList || `<p class="meta profile-custom-exercise-empty">등록된 커스텀 운동이 없습니다.</p>`}
      </div>
      <p class="meta profile-custom-exercise-empty hidden" id="profileCustomExerciseFilteredEmpty">검색 결과가 없습니다.</p>
    </div>

    <details class="card profile-collapsible-card profile-custom-create-card">
      <summary class="profile-collapsible-summary">
        <div class="profile-collapsible-summary-copy">
          <p class="profile-kicker">ADD NEW</p>
          <h3>새 커스텀 운동 추가</h3>
        </div>
        ${renderProfileExpandChevron()}
      </summary>
      <div class="profile-collapsible-body">
        <div class="grid-2">
          <div class="field">
            <label for="customNameKo">한글명</label>
            <input id="customNameKo" placeholder="예: 케이블 크런치" />
          </div>
          <div class="field">
            <label for="customNameEn">영문명</label>
            <input id="customNameEn" placeholder="Cable Crunch" />
          </div>
          <div class="field">
            <label for="customAbbr">약어</label>
            <input id="customAbbr" placeholder="CC" />
          </div>
          <div class="field">
            <label for="openCustomPrimaryMusclePickerBtn">주요 부위</label>
            <input id="customPrimaryMuscle" type="hidden" />
            ${renderSearchActionButton({
              id: "openCustomPrimaryMusclePickerBtn",
              classNames: ["custom-muscle-picker-trigger", "analytics-chart-change-exercise-btn", "analytics-chart-change-exercise-btn-block"],
              kicker: "주요 부위 검색",
              value: "주요 부위를 선택하세요",
              valueId: "customPrimaryMuscleSummary",
              attrs: {
                "data-muscle-target": "primary",
                "aria-controls": "customMusclePickerModal",
                "aria-haspopup": "dialog",
              },
            })}
            <div class="custom-muscle-chip-list" id="customPrimaryMuscleDisplay">
              <p class="meta custom-muscle-selection-empty">선택된 주요 부위가 없습니다.</p>
            </div>
          </div>
          <div class="field">
            <label for="customAliases">별칭(쉼표 구분)</label>
            <input id="customAliases" placeholder="복근, crunch, cable crunch" />
          </div>
          <div class="field">
            <label for="openCustomSecondaryMusclePickerBtn">보조 부위</label>
            <input id="customSecondaryMuscles" type="hidden" />
            ${renderSearchActionButton({
              id: "openCustomSecondaryMusclePickerBtn",
              classNames: ["custom-muscle-picker-trigger", "analytics-chart-change-exercise-btn", "analytics-chart-change-exercise-btn-block"],
              kicker: "보조 부위 검색",
              value: "보조 부위를 선택하세요",
              valueId: "customSecondaryMusclesSummary",
              attrs: {
                "data-muscle-target": "secondary",
                "aria-controls": "customMusclePickerModal",
                "aria-haspopup": "dialog",
              },
            })}
            <div class="custom-muscle-chip-list" id="customSecondaryMusclesDisplay">
              <p class="meta custom-muscle-selection-empty">선택된 보조 부위가 없습니다.</p>
            </div>
          </div>
        </div>
        <div class="row checkbox-row custom-exercise-checkbox-row">
          <label class="checkbox-label"><input id="customWeightRequired" type="checkbox" checked /><span class="checkbox-text">중량 입력 필요</span></label>
          <label class="checkbox-label"><input id="customBodyweight" type="checkbox" /><span class="checkbox-text">체중 운동</span></label>
        </div>
        <div class="row button-row profile-action-row">
          <button id="createCustomExerciseBtn">커스텀 운동 저장</button>
        </div>
        <p class="error" id="customExerciseError"></p>
      </div>
    </details>

    <div class="card profile-account-card">
      ${renderProfileSectionHeading("ACCOUNT & SECURITY", "계정 및 보안")}
      <div class="row button-row profile-account-action-row">
        <button class="secondary" id="logoutBtn">로그아웃</button>
      </div>
      <details class="profile-inline-collapsible">
        <summary class="profile-inline-collapsible-summary">
          <div>
            <strong>비밀번호 변경</strong>
            <p class="small">필요할 때만 열어서 변경합니다.</p>
          </div>
          ${renderProfileExpandChevron()}
        </summary>
        <div class="profile-inline-collapsible-body">
          <div class="grid-2">
            <div class="field">
              <label for="newPasswordInput">새 비밀번호</label>
              <input id="newPasswordInput" type="password" placeholder="새 비밀번호" />
            </div>
            <div class="field">
              <label for="newPasswordConfirmInput">새 비밀번호 확인</label>
              <input id="newPasswordConfirmInput" type="password" placeholder="새 비밀번호 확인" />
            </div>
          </div>
          <div class="row button-row profile-action-row">
            <button class="secondary" id="changePasswordBtn">비밀번호 변경</button>
          </div>
          <p class="small profile-account-note">보안 정책상 최근 재인증이 필요할 수 있습니다.</p>
        </div>
      </details>
      <div class="profile-danger-zone">
        <div>
          <strong>회원탈퇴</strong>
          <p class="small">계정을 완전히 삭제하며 복구할 수 없습니다.</p>
        </div>
        <button class="danger" id="deleteAccountBtn">회원탈퇴</button>
      </div>
    </div>
  `;
}

export function renderSessionPanel({
  workout,
  activeSession,
  previousByExercise = {},
  sessionItemEditOrder = null,
  sessionItemReorderOrder = null,
  inlineNotice = null,
}) {
  if (!workout?.meta) {
    return `
      <div class="card">
        <h3>진행 중 운동을 찾을 수 없습니다</h3>
        <button id="closeSessionBtn">돌아가기</button>
      </div>
    `;
  }

  const itemEntries = Object.entries(workout.items || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
  const totalItemCount = itemEntries.length;
  const restEnabled = activeSession?.rest?.enabled ?? true;
  const rawEditTargetOrder = sessionItemEditOrder;
  const parsedEditTargetOrder =
    rawEditTargetOrder === null || rawEditTargetOrder === undefined || rawEditTargetOrder === ""
      ? null
      : Number(rawEditTargetOrder);
  const hasEditTarget =
    Number.isInteger(parsedEditTargetOrder) &&
    parsedEditTargetOrder >= 0 &&
    !!workout.items?.[String(parsedEditTargetOrder)];
  const editTargetOrder = hasEditTarget ? parsedEditTargetOrder : null;
  const rawReorderTargetOrder = sessionItemReorderOrder;
  const parsedReorderTargetOrder =
    rawReorderTargetOrder === null || rawReorderTargetOrder === undefined || rawReorderTargetOrder === ""
      ? null
      : Number(rawReorderTargetOrder);
  const hasReorderTarget =
    Number.isInteger(parsedReorderTargetOrder) &&
    parsedReorderTargetOrder >= 0 &&
    !!workout.items?.[String(parsedReorderTargetOrder)];
  const reorderTargetOrder = hasReorderTarget ? parsedReorderTargetOrder : null;
  const editTargetItem = editTargetOrder !== null ? workout.items?.[String(editTargetOrder)] : null;

  function renderSessionEditorPanel({ mode = "add", order = null, item = null } = {}) {
    const isReplace = mode === "replace";
    const modeText = isReplace
      ? `편집 대상: ${Number(order) + 1}. ${escapeHtml(item?.nameSnapshot || "운동")}`
      : "신규 운동을 현재 진행 운동에 추가합니다.";
    const submitLabel = isReplace ? "운동 편집" : "운동 추가";
    const setsValue = Math.min(20, Math.max(1, Number(item?.setsPlan || 3)));
    const restValue = Math.min(MAX_REST_SEC, Math.max(MIN_REST_SEC, Number(item?.restSec || 90)));

    return `
      <div id="sessionAddPanel" class="${renderSafeClassList(["hidden", isReplace ? "session-item-change-editor" : "card"])}">
        <p class="small session-add-mode-text" id="sessionAddModeText">${modeText}</p>
        <div class="row analytics-search-row session-add-search-row">
          <div class="field">
            <label for="sessionAddExerciseId">운동 종목</label>
            <input
              id="sessionAddExerciseId"
              autocomplete="off"
              class="session-add-exercise-search-input"
              placeholder="벤치, 삼두, OHP ..."
            />
          </div>
        </div>
        <p class="small session-add-selected-name" id="sessionAddSelectedExerciseName">선택된 운동 없음</p>
        <div class="program-item-search-results" id="sessionAddExerciseResults"></div>
        <div class="row">
          <div class="field">
            <label for="sessionAddSets">세트</label>
            <input id="sessionAddSets" type="number" min="1" max="20" value="${setsValue}" />
          </div>
          <div class="field">
            <label for="sessionAddRestSec">휴식(초)</label>
            <input id="sessionAddRestSec" type="number" min="${MIN_REST_SEC}" max="${MAX_REST_SEC}" value="${restValue}" />
          </div>
        </div>
        <div class="row button-row session-add-submit-row" style="margin-top:10px">
          <button class="primary session-add-submit-btn" id="addSessionItemBtn">${submitLabel}</button>
          ${isReplace
        ? `<button class="secondary cancel-session-item-change-btn" data-order="${renderSafeIndexAttr(order)}">취소</button>`
        : `<button class="secondary" id="closeSessionAddPanelBtn" type="button">닫기</button>`
      }
        </div>
      </div>
    `;
  }

  const itemHtml = itemEntries
    .map(([order, item]) => {
      const isActiveItem = Number(order) === Number(activeSession?.current?.order);
      const isEditTarget = editTargetOrder !== null && Number(order) === editTargetOrder;
      const isReorderTarget = reorderTargetOrder !== null && Number(order) === reorderTargetOrder;
      const previousRecord = previousByExercise?.[item.exerciseId] || null;
      const itemOrder = Number(order);
      const canMoveUp = itemOrder > 0;
      const canMoveDown = itemOrder < Math.max(0, totalItemCount - 1);

      // Build previous-record summary (accordion)
      const prevSets = Object.entries(previousRecord?.item?.sets || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
      const previousRecordDateText = formatDateWithWeekday(previousRecord?.date);
      let previousRecordHtml = "";
      if (prevSets.length > 0) {
        const bestSet = pickBestSet(
          prevSets.map(([, set]) => set),
          { weightRequired: item.weightRequiredSnapshot !== false },
        );
        const bestSetSummary = item.weightRequiredSnapshot === false
          ? `최고 체중 × ${bestSet.reps}회`
          : `최고 ${bestSet.weight}kg × ${bestSet.reps}회`;
        const totalSetSummary = `총 ${prevSets.length}세트`;
        const prevDetailRows = prevSets.map(([setIndex, set]) => {
          const weightText = item.weightRequiredSnapshot === false ? "체중" : set.weight ?? "-";
          const repsText = set.reps ?? "-";
          return `
            <p class="small session-prev-row">
              <span class="session-prev-row-label">${Number(setIndex) + 1}set</span>
              <span class="session-prev-row-value">${escapeHtml(String(weightText))}${item.weightRequiredSnapshot === false ? "" : "kg"} x ${escapeHtml(String(repsText))}</span>
            </p>
          `;
        }).join("");
        previousRecordHtml = `
          <details class="session-prev-record">
            <summary class="session-prev-summary">
              <span class="session-prev-summary-copy">
                <span class="session-prev-summary-head">
                  <span class="session-prev-summary-label">이전 기록</span>
                  ${previousRecordDateText ? `<span class="session-prev-summary-date">${escapeHtml(previousRecordDateText)}</span>` : ""}
                </span>
                <span class="session-prev-summary-value">${escapeHtml(bestSetSummary)}</span>
                <span class="session-prev-summary-meta">${escapeHtml(totalSetSummary)}</span>
              </span>
              <span class="session-prev-indicator" aria-hidden="true">
                <svg class="session-prev-indicator-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M6 9l6 6 6-6"></path>
                </svg>
              </span>
            </summary>
            <div class="session-prev-detail">${prevDetailRows}</div>
          </details>
        `;
      }

      const sortedSets = Object.entries(item.sets || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
      const lastSetIndex = sortedSets.length > 0 ? Number(sortedSets[sortedSets.length - 1][0]) : -1;
      const lastSet = lastSetIndex >= 0 ? item.sets?.[String(lastSetIndex)] : null;
      const showDropSetButton = item.weightRequiredSnapshot !== false && lastSetIndex >= 0;
      const dropSetEnabled =
        showDropSetButton &&
        typeof lastSet?.weight === "number" &&
        Number.isFinite(lastSet.weight);
      const setHtml = sortedSets
        .map(
          ([setIndex, set]) => {
            const numericSetIndex = Number(setIndex);
            const isActiveSet =
              Number(order) === Number(activeSession?.current?.order) &&
              numericSetIndex === Number(activeSession?.current?.setIndex);
            const hasRepsValue = typeof set?.reps === "number" && Number.isFinite(set.reps);
            const hasWeightValue =
              item.weightRequiredSnapshot === false ||
              (typeof set?.weight === "number" && Number.isFinite(set.weight));
            const isDone = !!set?.done;
            const canToggleDone = isDone || (hasRepsValue && hasWeightValue);
            const doneButtonToneClass = isDone
              ? "success"
              : isActiveSet && canToggleDone
                ? ""
                : "secondary";
            const doneButtonTitle = isDone
              ? "완료 상태를 해제합니다."
              : canToggleDone
                ? "세트를 완료 처리합니다."
                : item.weightRequiredSnapshot === false
                  ? "횟수를 입력해야 완료할 수 있습니다."
                  : "무게와 횟수를 모두 입력해야 완료할 수 있습니다.";
            const doneButtonAriaLabel = isDone
              ? "완료 해제 버튼. 다시 누르면 완료 상태가 해제됩니다."
              : doneButtonTitle;

            const setLabel = `<span class="set-grid-label">${numericSetIndex + 1}세트</span>`;

            return `
            <div class="${renderSafeClassList(["set-grid", isActiveSet ? "active-set" : "", isDone ? "done-set" : ""])}" data-order="${renderSafeIndexAttr(order)}" data-set-index="${renderSafeIndexAttr(setIndex)}">
              ${setLabel}
              ${item.weightRequiredSnapshot !== false
                ? `
              <div class="field set-weight-field">
                <label>kg</label>
                <input type="number" min="0" max="${MAX_WEIGHT}" step="0.5" value="${set.weight ?? ""}" data-role="weight" />
              </div>`
                : ""
              }
              <div class="field set-reps-field">
                <label>횟수</label>
                <input type="number" min="0" max="${MAX_REPS}" step="1" value="${set.reps ?? ""}" data-role="reps" />
              </div>
              <div class="set-actions">
                <button
                  class="${renderSafeClassList([doneButtonToneClass, "set-done-btn"])}"
                  data-action="set-done"
                  type="button"
                  title="${escapeHtml(doneButtonTitle)}"
                  aria-pressed="${isDone ? "true" : "false"}"
                  aria-label="${escapeHtml(doneButtonAriaLabel)}"
                >
                  <span class="set-done-btn-main">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="set-btn-icon set-done-icon set-done-icon-check" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="set-btn-icon set-done-icon set-done-icon-undo" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 3v6h6"/></svg>
                    <span class="set-done-btn-label">${isDone ? "완료 해제" : "완료"}</span>
                  </span>
                </button>
                <button class="btn-danger-subtle set-remove-btn" data-action="set-remove" aria-label="세트 삭제" title="세트 삭제">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="set-btn-icon"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                </button>
              </div>
            </div>
          `;
          },
        )
        .join("");

      return `
	        <div class="${renderSafeClassList(["card", "session-item-card", isActiveItem ? "active-item-card" : ""])}" data-order="${renderSafeIndexAttr(order)}">
	          <div class="session-item-head">
	            <div class="session-item-head-left">
	              <span class="session-item-order-badge">${itemOrder + 1}</span>
	              <h3>${escapeHtml(item.nameSnapshot)}</h3>
	            </div>
	            <button class="session-item-more-btn" data-order="${renderSafeIndexAttr(order)}" type="button" aria-label="더보기" title="운동 옵션" aria-haspopup="menu" aria-expanded="false" aria-controls="sessionContextMenu-${renderSafeIndexAttr(order)}">
	              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
	            </button>
	          </div>
	          <div class="session-item-context-menu hidden" data-order="${renderSafeIndexAttr(order)}" id="sessionContextMenu-${renderSafeIndexAttr(order)}" role="menu" aria-label="운동 옵션">
	            <div class="session-item-context-actions session-item-context-actions-stack" role="group" aria-label="운동 편집 옵션">
              <button class="${renderSafeClassList(["session-ctx-btn", isReorderTarget ? "is-active" : ""])}" data-action="toggle-session-item-reorder" data-order="${renderSafeIndexAttr(order)}" type="button" role="menuitem">
	                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" class="ctx-icon"><path d="M8 7h8"/><path d="M8 17h8"/><path d="m12 3 4 4-4 4"/><path d="m12 13-4 4 4 4"/></svg>
	                순서 변경
	              </button>
	              <button class="session-ctx-btn change-session-item-btn" data-order="${renderSafeIndexAttr(order)}" type="button" role="menuitem">
	                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ctx-icon"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
	                운동 편집
	              </button>
	              <button class="session-ctx-btn btn-danger-subtle remove-session-item-btn" data-order="${renderSafeIndexAttr(order)}" type="button" role="menuitem">
	                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ctx-icon"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
	                운동 삭제
	              </button>
	            </div>
	          </div>
	          ${isReorderTarget
          ? `
	              <div class="session-item-reorder-bar" data-role="session-item-reorder-bar" data-order="${renderSafeIndexAttr(order)}">
	                <div class="session-item-reorder-bar-head">
	                  <span class="session-item-reorder-bar-copy">
	                    <span class="session-item-reorder-bar-label">순서 변경 중</span>
	                    <span class="session-item-reorder-bar-meta">위나 아래로 한 칸씩 위치를 바꿀 수 있습니다.</span>
	                  </span>
	                  <span class="session-item-context-order-chip">${itemOrder + 1} / ${totalItemCount}</span>
	                </div>
	                <div class="session-item-reorder-bar-actions">
	                  <button
	                    class="session-reorder-step-btn"
	                    data-action="move-session-item"
	                    data-direction="up"
	                    data-order="${renderSafeIndexAttr(order)}"
	                    type="button"
	                    aria-label="운동을 위로 이동"
	                    title="한 단계 위로 이동"
	                    ${canMoveUp ? "" : "disabled"}
	                  >
	                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" class="ctx-icon"><path d="M12 19V5"/><path d="m6 11 6-6 6 6"/></svg>
	                    위로 이동
	                  </button>
	                  <button
	                    class="session-reorder-step-btn"
	                    data-action="move-session-item"
	                    data-direction="down"
	                    data-order="${renderSafeIndexAttr(order)}"
	                    type="button"
	                    aria-label="운동을 아래로 이동"
	                    title="한 단계 아래로 이동"
	                    ${canMoveDown ? "" : "disabled"}
	                  >
	                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" class="ctx-icon"><path d="M12 5v14"/><path d="m18 13-6 6-6-6"/></svg>
	                    아래로 이동
	                  </button>
	                </div>
	                <div class="session-item-reorder-done-row">
		                  <button class="secondary session-item-reorder-done-btn" data-action="close-session-item-reorder" data-order="${renderSafeIndexAttr(order)}" type="button">
		                    순서 변경 완료
		                  </button>
		                </div>
		              </div>
	            `
          : ""}
	          ${isEditTarget ? renderSessionEditorPanel({ mode: "replace", order: Number(order), item }) : ""}
          ${previousRecordHtml}
          ${setHtml}
          <div class="row button-row session-set-footer">
            <button class="secondary add-session-set-btn" data-order="${renderSafeIndexAttr(order)}">세트 추가 +</button>
            ${showDropSetButton
          ? `<button class="secondary set-drop-btn add-drop-session-set-btn" data-order="${renderSafeIndexAttr(order)}" data-set-index="${renderSafeIndexAttr(lastSetIndex)}" data-action="set-drop" ${dropSetEnabled ? "" : "disabled"}>드랍세트 추가</button>`
          : ""
        }
          </div>
        </div>
      `;
    })
    .join("");

  return `
    <div id="sessionInlineNoticeMount">${renderSessionInlineNotice(inlineNotice)}</div>
    <div class="card session-hero-card">
      <div class="session-hero-top">
        <div class="session-hero-actions">
          <button
            class="session-hero-rest-btn"
            id="toggleRestEnabledBtn"
            type="button"
            aria-pressed="${restEnabled ? "true" : "false"}"
            title="${restEnabled ? "자동 휴식 타이머 끄기" : "자동 휴식 타이머 켜기"}"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="session-hero-rest-icon"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
            휴식 ${restEnabled ? "ON" : "OFF"}
          </button>
          <button class="session-hero-cancel-btn btn-danger-subtle" id="openSessionCancelModalBtn" type="button">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="session-hero-cancel-icon"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86l-7.5 13A2 2 0 0 0 4.53 20h14.94a2 2 0 0 0 1.74-3.14l-7.5-13a2 2 0 0 0-3.42 0z"/></svg>
            운동 취소
          </button>
        </div>
      </div>
      <p class="session-hero-timer"><strong id="sessionElapsedText">${formatActiveSessionElapsed(workout?.meta?.startedAt)}</strong></p>
      <p class="session-hero-start">시작: ${formatDateTime(workout.meta.startedAt)}</p>
    </div>
    ${itemHtml || `<p class="meta">운동 아이템이 없습니다.</p>`}
    <div class="session-add-card" style="margin-bottom: 24px;">
      <button class="btn-soft-primary session-add-toggle-btn" id="toggleSessionAddPanelBtn" type="button">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="session-add-toggle-icon"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        <span class="session-add-toggle-label">운동 추가하기</span>
      </button>
      ${!editTargetItem ? renderSessionEditorPanel({ mode: "add" }) : ""}
    </div>
  `;
}

export function renderSessionInlineNotice(notice) {
  const title = String(notice?.title || "").trim();
  const meta = String(notice?.meta || "").trim();
  const message = String(notice?.message || "").trim();

  if (!title && !meta && !message) {
    return "";
  }

  return `
    <div class="session-inline-notice-wrap">
      <div class="modal-sheet session-inline-notice" role="status" aria-live="polite">
        <div class="modal-sheet-header">
          <h3>${escapeHtml(title || "자동 저장에 실패했습니다")}</h3>
          <button class="secondary modal-close-btn" type="button" data-action="dismiss-session-inline-notice" aria-label="경고 닫기">닫기</button>
        </div>
        ${meta ? `<p class="modal-sheet-meta">${escapeHtml(meta)}</p>` : ""}
        ${message ? `<p class="session-action-modal-copy">${escapeHtml(message)}</p>` : ""}
      </div>
    </div>
  `;
}

export function renderWorkoutCompleteSummary(workout, options = {}) {
  const totalSets = Object.values(workout?.items || {}).reduce((total, item) => {
    return total + countLoggedItemSets(item);
  }, 0);

  const durationStr = formatWorkoutDuration(workout);
  const heatmapData = createWorkoutLogHeatmap(workout);

  // Main Sets 계산
  const mainSets = [];
  for (const item of Object.values(workout?.items || {})) {
    const setsObj = item?.sets || {};
    let itemVolume = 0;
    let totalItemSets = 0;

    for (const set of Object.values(setsObj)) {
      if (set.reps > 0 || set.weight > 0) {
        totalItemSets++;
        itemVolume += (set.weight || 0) * (set.reps || 0);
      }
    }

    if (totalItemSets > 0) {
      const bestSet = pickBestSet(setsObj, {
        weightRequired: item.weightRequiredSnapshot !== false,
      });
      mainSets.push({
        name: item.nameSnapshot || "운동",
        maxWeight: bestSet.weight,
        maxReps: bestSet.reps,
        itemVolume,
        totalItemSets,
        weightRequired: item.weightRequiredSnapshot !== false,
      });
    }
  }

  const topEx = mainSets.sort((a, b) => b.itemVolume - a.itemVolume).slice(0, 3);
  const totalExercises = mainSets.length;
  const topSetMetaText = `${topEx.length} / ${totalExercises}`;

  const exHtml = topEx.map(ex => `
    <div class="share-main-set-item">
      <div class="share-main-set-copy">
        <div class="share-main-set-head">
          <span class="share-main-set-name">${escapeHtml(ex.name)}</span>
          <span class="share-main-set-count">${escapeHtml(ex.totalItemSets)}세트</span>
        </div>
      </div>
      <div class="share-main-set-highlight">
        <span class="share-main-set-highlight-label">최고</span>
        <span class="share-main-set-highlight-value">${escapeHtml(
    ex.weightRequired
      ? ex.maxWeight > 0
        ? `${ex.maxWeight}kg × ${ex.maxReps}회`
        : `${ex.maxReps}회`
      : `체중 × ${ex.maxReps}회`,
  )}</span>
      </div>
    </div>
  `).join("");

  const dateStr = workout?.meta?.date || new Date().toISOString().split('T')[0];

  return `
    <div class="workout-summary-overlay" id="workoutSummaryOverlay">

      <!-- 캡처될 영역 -->
      <div class="share-card" id="shareCardElement">

        <!-- 헤더 -->
        <div class="share-card-header">
          <h2 class="share-title">${escapeHtml(dateStr)}</h2>
        </div>

        <!-- 핵심 스탯 -->
        ${renderShareStatsRow([
    { value: totalExercises, suffix: "", label: "운동 종목" },
    { value: totalSets, suffix: "세트", label: "총 세트" },
    { value: durationStr, suffix: "", label: "운동 시간" },
  ])}

	        <!-- 히트맵 -->
	        ${renderShareHeatmapSection(heatmapData)}

        <!-- 메인 세트 -->
        ${topEx.length > 0 ? `
        <div class="share-main-sets-section">
          <div class="share-main-sets-head">
            <p class="share-section-label">주요 세트</p>
            <span class="share-section-meta">${escapeHtml(topSetMetaText)}</span>
          </div>
          <div class="share-main-sets-list">
            ${exHtml}
          </div>
        </div>
        ` : ''}

        <!-- 워터마크 -->
        <div class="share-watermark">
          <span>LiftFlow</span>
        </div>
      </div>

      <!-- 하단 액션 버튼 (캡처 스킵됨) -->
      <div class="share-action-area" data-html2canvas-ignore="true">
        <button class="share-ig-btn" id="shareIgBtn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="share-btn-icon"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          인스타 스토리에 공유
        </button>
        <div class="share-action-row">
          <button class="secondary share-download-btn" id="shareDownloadBtn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="share-btn-icon"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            이미지 저장
          </button>
          <button class="secondary share-close-btn" id="shareCloseBtn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="share-btn-icon"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            닫기
          </button>
        </div>
      </div>
    </div>
  `;
}
